<template>
  <div class="model-comparison">
    <!-- Sign-in Required -->
    <div v-if="!hasSession" class="alert alert-warning d-flex align-items-center">
      <i class="bi bi-lock me-3 fs-4"></i>
      <div class="flex-grow-1">
        <strong>Sign in required</strong>
        <p class="mb-0 small">Please sign in to access weather model comparison data.</p>
      </div>
      <a class="btn btn-primary btn-sm" href="#" onclick="window.dialogManager?.showSignIn(); return false;">
        <i class="bi bi-box-arrow-in-right me-1"></i>Sign In
      </a>
    </div>

    <template v-else>
    <!-- Controls Bar -->
    <div class="controls-bar">
      <!-- Location Picker -->
      <div class="control-group location-group">
        <label class="control-label">Location</label>
        <div class="location-input-wrapper">
          <input 
            type="text" 
            class="form-control form-control-sm location-input"
            v-model="locationText"
            @keyup.enter="geocodeLocation"
            placeholder="Enter location..."
          />
          <button class="btn btn-sm btn-outline-primary" @click="geocodeLocation" :disabled="isGeocoding">
            <i class="bi" :class="isGeocoding ? 'bi-arrow-repeat spin-icon' : 'bi-search'"></i>
          </button>
          <button class="btn btn-sm btn-outline-secondary" @click="useCurrentLocation" :disabled="isGeocoding">
            <i class="bi bi-geo"></i>
          </button>
        </div>
        <div v-if="location" class="location-display">
          <i class="bi bi-geo-alt-fill"></i>
          {{ location.name || `${location.lat?.toFixed(4)}, ${location.lon?.toFixed(4)}` }}
        </div>
      </div>

      <!-- Forecast Basis + Date Range combined row -->
      <div class="control-group">
        <label class="control-label">Forecast Basis</label>
        <div class="date-inputs">
          <input type="date" class="form-control form-control-sm" v-model="forecastBasisDate" title="Forecast basis date" />
          <input type="time" class="form-control form-control-sm forecast-time-input" v-model="forecastBasisTime" step="3600" title="Hour (00Z = midnight UTC)" />
          <select class="form-select form-select-sm tz-select" v-model="forecastBasisTimezone" title="Timezone">
            <option value="utc">UTC</option>
            <option value="local">Local</option>
          </select>
          <button class="btn btn-sm" :class="isForecastBasisNow ? 'btn-secondary' : 'btn-outline-secondary'" @click="setForecastBasisNow" title="Use latest available models (no historical basis)">
            <i class="bi bi-broadcast"></i> Live
          </button>
        </div>
        <div class="forecast-basis-hint" v-if="!isForecastBasisNow">
          <i class="bi bi-info-circle"></i> Historical: models as of {{ formatForecastBasis }}
        </div>
      </div>

      <!-- Date Range -->
      <div class="control-group">
        <label class="control-label">Date Range</label>
        <div class="date-inputs">
          <input type="date" class="form-control form-control-sm" v-model="startDate" />
          <span class="date-separator">to</span>
          <input type="date" class="form-control form-control-sm" v-model="endDate" />
        </div>
        <div class="quick-dates">
          <a href="#" class="quick-date-link" @click.prevent="setDateRange('today')">Today</a>
          <a href="#" class="quick-date-link" @click.prevent="setDateRange('3days')">3 Days</a>
          <a href="#" class="quick-date-link" @click.prevent="setDateRange('week')">Week</a>
          <a href="#" class="quick-date-link" @click.prevent="setDateRange('2weeks')">2 Weeks</a>
        </div>
      </div>

      <!-- Resolution + Units on one line -->
      <div class="control-group">
        <label class="control-label">Resolution</label>
        <div class="btn-group btn-group-sm">
          <button 
            class="btn" 
            :class="timeResolution === 'minutes' ? 'btn-primary' : 'btn-outline-secondary'"
            @click="timeResolution = 'minutes'"
          >Minutes</button>
          <button 
            class="btn" 
            :class="timeResolution === 'hours' ? 'btn-primary' : 'btn-outline-secondary'"
            @click="timeResolution = 'hours'"
          >Hourly</button>
          <button 
            class="btn" 
            :class="timeResolution === 'days' ? 'btn-primary' : 'btn-outline-secondary'"
            @click="timeResolution = 'days'"
          >Daily</button>
          <select v-if="timeResolution === 'minutes'" v-model="minuteInterval" class="form-select form-select-sm minute-interval-select" title="Minute interval">
            <option value="1">1 min</option>
            <option value="5">5 min</option>
            <option value="10">10 min</option>
            <option value="15">15 min</option>
            <option value="30">30 min</option>
          </select>
          <select v-model="unitGroup" class="form-select form-select-sm unit-select" title="Unit system">
            <option value="metric">Metric</option>
            <option value="us">US</option>
            <option value="uk">UK</option>
            <option value="base">SI</option>
          </select>
        </div>
      </div>

      <!-- API Options -->
      <div class="control-group options-group">
        <label class="control-label">Options</label>
        <div class="option-checkboxes">
          <label class="option-checkbox" :title="!isForecastBasisNow ? 'Not used for historical forecast' : 'Use forecast data instead of observations'">
            <input type="checkbox" v-model="optionUsefcst" :disabled="!isForecastBasisNow" />
            <span :style="!isForecastBasisNow ? 'opacity:0.5' : ''">usefcst</span>
          </label>
          <label class="option-checkbox" title="Disable interpolation between model grid points">
            <input type="checkbox" v-model="optionNointerpolate" />
            <span>nointerp</span>
          </label>
          <label class="option-checkbox" title="Include model attribution in response">
            <input type="checkbox" v-model="optionAttribution" />
            <span>attrib</span>
          </label>
          <label class="option-checkbox" title="Track which model provides each element value">
            <input type="checkbox" v-model="optionTrackModelChanges" />
            <span>trackmodels</span>
          </label>
          <label class="option-checkbox" title="Fetch actual observations for comparison" :class="{ disabled: isForecastBasisNow }">
            <input type="checkbox" v-model="optionCompareWithObs" :disabled="isForecastBasisNow" />
            <span>vs obs</span>
          </label>
        </div>
      </div>

      <!-- Fetch Button + Status -->
      <div class="control-group fetch-group">
        <button 
          class="btn btn-sm fetch-btn"
          :class="needsRefresh ? 'btn-warning' : 'btn-primary'"
          @click="fetchData"
          :disabled="!canFetch || isLoading"
          :title="needsRefresh ? 'Settings changed - click to refresh' : 'Fetch and compare model data'"
        >
          <i class="bi" :class="isLoading ? 'bi-arrow-repeat spin-icon' : (needsRefresh ? 'bi-arrow-repeat' : 'bi-download')"></i>
          {{ isLoading ? 'Loading...' : (needsRefresh ? 'Refresh' : 'Compare') }}
        </button>

        <button class="btn btn-xs btn-outline-secondary" @click="showHelpModal = true" title="Help">
          <i class="bi bi-question-circle"></i>
        </button>
      </div>
    </div>

    <!-- Main Content Area -->
    <div class="comparison-content">
      <!-- Model Selection Panel (Left) -->
      <div class="model-panel" :class="{ collapsed: modelPanelCollapsed }">
        <div class="panel-header" @click="modelPanelCollapsed = !modelPanelCollapsed">
          <span>Models</span>
          <span class="selected-count">{{ selectedModelCount }}/{{ totalModelCount }}</span>
          <i class="bi" :class="modelPanelCollapsed ? 'bi-chevron-right' : 'bi-chevron-down'"></i>
        </div>
        
        <div class="panel-content" v-show="!modelPanelCollapsed">
          <!-- Mode Toggle Bar -->
          <div class="mode-toggle-bar">
            <button 
              class="mode-btn" 
              :class="{ active: !showModelRuns }" 
              @click="onModelRunsToggle(false)">
              <i class="bi bi-box me-1"></i>Models
            </button>
            <button 
              class="mode-btn" 
              :class="{ active: showModelRuns }" 
              @click="onModelRunsToggle(true)">
              <i class="bi me-1" :class="modelRunsLoading ? 'bi-arrow-repeat spin-icon' : 'bi-clock-history'"></i>Model Runs
            </button>
          </div>
          
          <!-- Selected Model Runs List -->
          <div v-if="showModelRuns && selectedModelRuns.length > 0" class="selected-runs-list">
            <div class="selected-runs-header">
              <span>Selected ({{ selectedModelRuns.length }})</span>
              <a href="#" class="clear-link" @click.prevent="clearSelectedRuns">Clear</a>
            </div>
            <div class="selected-runs-items">
              <div v-for="(run, idx) in selectedModelRuns" :key="idx" class="selected-run-item" :style="{ borderLeftColor: getRunColor(idx) }">
                <span class="run-model-name">{{ run.displayName }}</span>
                <span class="run-time">{{ formatRunTime(run.runTime) }}</span>
                <button class="btn btn-xs" @click="removeSelectedRun(idx)" title="Remove">
                  <i class="bi bi-x"></i>
                </button>
              </div>
            </div>
          </div>

          <!-- V2 Models Loading indicator -->
          <div v-if="v2ModelsLoading" class="loading-models">
            <i class="bi bi-arrow-repeat spin-icon"></i> Loading V2 models from API...
          </div>

          <!-- Model Groups -->
          <div class="model-groups">
            <!-- Clear All Link -->
            <div v-if="!showModelRuns" class="models-header-bar">
              <a href="#" class="clear-link" @click.prevent="selectPreset('none')" v-if="selectedModels.length > 0">clear</a>
            </div>
            
            <!-- Model Combinations (Default Models) -->
            <div class="model-group" v-if="!showModelRuns">
              <div class="group-header combinations-header" @click="toggleGroup('combinations')">
                <i class="bi bi-stack me-1"></i>
                <span>Model Combinations</span>
                <i class="bi ms-auto" :class="expandedGroups.combinations ? 'bi-chevron-down' : 'bi-chevron-right'"></i>
              </div>
              <div class="group-models" v-show="expandedGroups.combinations">
                <label class="model-checkbox combo-item combo-default-v1">
                  <input type="checkbox" v-model="selectedModels" value="default" />
                  <span class="model-color" style="background-color: #000000;"></span>
                  <span class="combo-label">
                    <span class="model-name">Default V1</span>
                    <span class="combo-desc">Legacy blended forecast</span>
                  </span>
                  <span class="combo-badge combo-badge-v1">V1</span>
                </label>
                <label class="model-checkbox combo-item combo-default-v2">
                  <input type="checkbox" v-model="selectedModels" value="default_v2" />
                  <span class="model-color" style="background-color: #2E7D32;"></span>
                  <span class="combo-label">
                    <span class="model-name">Default V2</span>
                    <span class="combo-desc">Current blended forecast</span>
                  </span>
                  <span class="combo-badge combo-badge-v2">V2</span>
                </label>
                <label class="model-checkbox combo-item combo-default-v2-ext">
                  <input type="checkbox" v-model="selectedModels" value="default_v2_extended" />
                  <span class="model-color" style="background-color: #6A1B9A;"></span>
                  <span class="combo-label">
                    <span class="model-name">Default V2 Extended</span>
                    <span class="combo-desc">V2 + ECMWF, ICON-Global, UKMET, AI</span>
                  </span>
                  <span class="combo-badge combo-badge-v2ext">V2+</span>
                </label>
                <!--
                <label class="model-checkbox combo-item combo-global">
                  <input type="checkbox" v-model="selectedModels" value="vcglobalblended" />
                  <span class="model-color" style="background-color: #1565C0;"></span>
                  <span class="combo-label">
                    <span class="model-name">VC Global Blended</span>
                    <span class="combo-desc">Worldwide coverage</span>
                  </span>
                  <span class="combo-badge combo-badge-global"><i class="bi bi-globe2"></i></span>
                </label>
                <label class="model-checkbox combo-item combo-conus">
                  <input type="checkbox" v-model="selectedModels" value="vcglobalblended_conus" />
                  <span class="model-color" style="background-color: #E65100;"></span>
                  <span class="combo-label">
                    <span class="model-name">VC Global Blended (CONUS)</span>
                    <span class="combo-desc">US-optimized blend</span>
                  </span>
                  <span class="combo-badge combo-badge-conus">US</span>
                </label>
                -->
              </div>
            </div>

            <!-- V2 Models (from API) - Standard Mode -->
            <div class="model-group" v-if="!showModelRuns">
              <div class="group-header v2-header" @click="toggleGroup('v2')">
                <i class="bi bi-lightning-fill me-1 text-success"></i>
                <span>V2 Models</span>
                <span class="group-count">{{ modelGroups.v2.length }}</span>
                <i class="bi ms-auto" :class="expandedGroups.v2 ? 'bi-chevron-down' : 'bi-chevron-right'"></i>
              </div>
              <div class="group-models" v-show="expandedGroups.v2">
                <div class="v2-quick-buttons" v-if="v2ModelsLoaded">
                  <button class="btn btn-xs btn-outline-success" @click.stop="addV2DefaultModels" title="Select: GFES, GFS, NAM, HRRR, ICON-EU, US Blended, NDFD">+ V2 Models</button>
                  <button class="btn btn-xs btn-outline-primary" @click.stop="addV2ExtendedModels" title="Select: V2 Models + ECMWF, ICON Global, UK Met, ECMWF AIFS, AI GFS">+ V2 Extended</button>
                </div>
                <div v-if="!v2ModelsLoaded && !v2ModelsLoading" class="no-models-msg">
                  <i class="bi bi-info-circle"></i> Click "Fetch Data" to load V2 models
                </div>
                <label v-for="model in modelGroups.v2" :key="`${model.id}-${locationKey}`" class="model-checkbox" :class="{ 'out-of-bounds': !isModelInBounds(model) }">
                  <input type="checkbox" v-model="selectedModels" :value="model.id" />
                  <span class="model-color" :style="{ backgroundColor: model.color }"></span>
                  <span class="model-name">{{ model.name }}</span>
                  <span v-if="!isModelInBounds(model)" class="out-bounds-icon" title="Model may not cover this location"><i class="bi bi-geo"></i></span>
                  <span class="model-badge badge-v2">V2</span>
                </label>
              </div>
            </div>
            
            <!-- V2 Models - Model Runs Mode -->
            <div class="model-runs-container" v-if="showModelRuns">
              <div v-if="modelRunsLoading" class="runs-loading">
                <i class="bi bi-arrow-repeat spin-icon me-1"></i> Loading available runs...
              </div>
              <div v-else-if="Object.keys(modelRunsData).length === 0" class="no-runs-msg">
                <i class="bi bi-info-circle me-1"></i> No model runs loaded. Click refresh to load available runs.
              </div>
              <div v-else class="runs-by-model">
                <div v-for="model in modelsWithRuns" :key="model.id" class="model-runs-group">
                  <div class="model-runs-title">
                    <span class="model-color-dot" :style="{ backgroundColor: model.color }"></span>
                    <span class="model-name">{{ model.name }}</span>
                  </div>
                  <div class="runs-by-date-container">
                    <div v-for="dateGroup in getRunsByDate(model.id)" :key="dateGroup.date" class="runs-date-group">
                      <div class="runs-date-label">{{ dateGroup.dateLabel }}</div>
                      <div class="runs-time-buttons">
                        <button 
                          v-for="run in dateGroup.runs" 
                          :key="run.runId"
                          class="run-time-btn"
                          :class="{ selected: isRunSelected(model.id, run.runId) }"
                          @click="toggleRunSelection(model.id, run)"
                          :title="formatRunTime(run.runTime)">
                          {{ formatRunHour(run.runTime) }}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Legacy Models -->
            <div class="model-group" v-if="!showModelRuns">
              <div class="group-header legacy-header" @click="toggleGroup('legacy')">
                <i class="bi bi-clock-history me-1 text-warning"></i>
                <span>Legacy Models</span>
                <span class="group-count">{{ modelGroups.legacy.length }}</span>
                <i class="bi ms-auto" :class="expandedGroups.legacy ? 'bi-chevron-down' : 'bi-chevron-right'"></i>
              </div>
              <div class="group-models" v-show="expandedGroups.legacy">
                <label v-for="model in modelGroups.legacy" :key="model.id" class="model-checkbox">
                  <input type="checkbox" v-model="selectedModels" :value="model.id" />
                  <span class="model-color" :style="{ backgroundColor: model.color }"></span>
                  <span class="model-name">{{ model.name }}</span>
                  <span class="model-badge badge-legacy">Legacy</span>
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Chart & Results Area (Center) -->
      <div class="results-area">
        <!-- Element Selector -->
        <div class="element-selector">
          <label class="control-label">Element:</label>
          <select class="form-select form-select-sm" v-model="selectedElement">
            <option v-for="el in availableElements" :key="el.id" :value="el.id">
              {{ el.name }}
            </option>
          </select>
          <button class="btn btn-sm btn-outline-secondary ms-1" @click="openElementPicker" title="Choose which elements to retrieve from API">
            <i class="bi bi-list-check"></i>
          </button>
          
          <label class="control-label ms-3">Baseline:</label>
          <select class="form-select form-select-sm" v-model="baselineModel">
            <option value="">None</option>
            <option v-for="model in selectedModelConfigs" :key="model.id" :value="model.id">
              {{ model.name }}{{ hasV2Version(model.id) ? ' ⚠️ Legacy' : '' }}{{ isV2Model(model.id) ? ' V2' : '' }}
            </option>
          </select>

          <button 
            class="btn btn-sm btn-outline-info ms-2"
            @click="runQuickCompare"
            :disabled="!canQuickCompare"
            title="Compare all models and elements against baseline to find issues"
          >
            <i class="bi" :class="quickCompareRunning ? 'bi-arrow-repeat spin-icon' : 'bi-lightning-charge'"></i>
            Quick Compare
          </button>

          <div class="view-toggle ms-auto">
            <div class="btn-group btn-group-sm">
              <button 
                class="btn" 
                :class="viewMode === 'chart' ? 'btn-primary' : 'btn-outline-secondary'"
                @click="viewMode = 'chart'"
              ><i class="bi bi-graph-up"></i></button>
              <button 
                class="btn" 
                :class="viewMode === 'table' ? 'btn-primary' : 'btn-outline-secondary'"
                @click="viewMode = 'table'"
              ><i class="bi bi-table"></i></button>
              <button 
                class="btn" 
                :class="viewMode === 'diff' ? 'btn-primary' : 'btn-outline-secondary'"
                @click="viewMode = 'diff'"
                :disabled="!baselineModel"
              ><i class="bi bi-arrows-expand"></i></button>
              <button 
                class="btn" 
                :class="viewMode === 'map' ? 'btn-primary' : 'btn-outline-secondary'"
                @click="viewMode = 'map'"
                title="Map View"
              ><i class="bi bi-map"></i></button>
              <button 
                class="btn" 
                :class="viewMode === 'split' ? 'btn-primary' : 'btn-outline-secondary'"
                @click="viewMode = 'split'"
                title="Chart + Map Split View"
              ><i class="bi bi-layout-split"></i></button>
              <button 
                class="btn" 
                :class="viewMode === 'chartgrid' ? 'btn-primary' : 'btn-outline-secondary'"
                @click="viewMode = 'chartgrid'"
                title="Chart + Grid Split View"
              ><i class="bi bi-layout-text-window-reverse"></i></button>
            </div>
          </div>
        </div>

        <!-- Chart View -->
        <div class="chart-container" v-show="viewMode === 'chart' || viewMode === 'diff'">
          <div class="comparison-chart" ref="chartContainer">
            <svg ref="chartSvg"></svg>
            <div class="chart-tooltip" ref="chartTooltip">
              <div class="tooltip-content"></div>
            </div>
          </div>
          
          <!-- Chart Legend -->
          <div class="chart-legend" v-if="hasData">
            <div 
              v-for="model in selectedModelConfigs" 
              :key="model.id" 
              class="legend-item"
              :class="{ 
                dimmed: highlightedModel && highlightedModel !== model.id,
                'is-ensemble': model.isEnsemble 
              }"
              @mouseenter="highlightModel(model.id)"
              @mouseleave="highlightModel(null)"
            >
              <span class="legend-color" :style="{ backgroundColor: model.color }"></span>
              <span class="legend-name">{{ model.name }}</span>
              <span class="legend-spread-indicator" v-if="modelHasSpread(model.id) && showEnsembleSpread">
                <i class="bi bi-graph-up"></i>
              </span>
            </div>
          </div>
        </div>

        <!-- Table View -->
        <div class="table-container" v-show="viewMode === 'table'">
          <table class="table table-sm table-striped table-hover">
            <thead>
              <tr>
                <th class="sticky-col">Time</th>
                <th 
                  v-for="model in selectedModelConfigs" 
                  :key="model.id"
                  :style="{ borderTop: `3px solid ${model.color}` }"
                  :class="{ 'legacy-model': hasV2Version(model.id), 'v2-model': isV2Model(model.id) }"
                >
                  {{ model.name }}
                  <span v-if="hasV2Version(model.id)" class="th-legacy" title="Legacy - V2 version available">⚠️</span>
                 
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in tableData" :key="row.datetime">
                <td class="sticky-col">{{ formatDateTime(row.datetime) }}</td>
                <td 
                  v-for="model in selectedModelConfigs" 
                  :key="model.id"
                  :class="getCellClass(row, model.id)"
                >
                  {{ formatValue(row[model.id]) }}
                  <div v-if="row._sources && row._sources[model.id]" class="cell-source" :title="row._sources[model.id]">{{ row._sources[model.id] }}</div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Map View -->
        <div class="mc-map-view" v-if="viewMode === 'map'">
          <div class="mc-map-wrap">
            <div class="mc-map" ref="mapEl"></div>
            <!-- Layer picker -->
            <div class="mc-map-layers">
              <button v-for="layer in mapLayerOptions" :key="layer.id" class="mc-map-layer-btn"
                :class="{ 'mc-map-layer-btn--active': activeMapLayer === layer.id }" @click="switchMapLayer(layer.id)"
                :title="layer.label">
                <i :class="layer.icon"></i>
                <span class="mc-map-layer-label">{{ layer.shortLabel }}</span>
              </button>
            </div>
            <!-- Model picker -->
            <div class="mc-map-model-picker" v-if="mapModelOptions.length > 1">
              <select class="form-select form-select-sm" v-model="activeMapModel" @change="switchMapModel">
                <option v-for="m in mapModelOptions" :key="m.id" :value="m.id">{{ m.name }}</option>
              </select>
            </div>
            <!-- Time controls -->
            <div class="mc-map-time">
              <button class="mc-map-time-btn" @click="mapStepTime(-activeLayerInterval)" :disabled="mapIsPlaying">
                <i class="bi bi-chevron-left"></i>
              </button>
              <button class="mc-map-time-btn" @click="mapPlayPause">
                <i class="bi" :class="mapIsPlaying ? 'bi-pause-fill' : 'bi-play-fill'"></i>
              </button>
              <div class="mc-map-time-display">
                <div class="mc-map-time-value">{{ mapTimeDisplay }}</div>
                <div class="mc-map-time-date">{{ mapDateDisplay }}</div>
              </div>
              <button class="mc-map-time-btn" @click="mapStepTime(activeLayerInterval)" :disabled="mapIsPlaying">
                <i class="bi bi-chevron-right"></i>
              </button>
              <button class="mc-map-time-btn" @click="mapResetToNow" :disabled="mapIsPlaying" title="Reset to now">
                <i class="bi bi-arrow-clockwise"></i>
              </button>
            </div>
            <!-- Empty overlay -->
            <div class="mc-map-empty" v-if="!hasData && !isLoading">
              <i class="bi bi-map icon-3x mb-3"></i>
              <h5>No Data Yet</h5>
              <p>Select models and click Compare to see map data.</p>
            </div>
          </div>
        </div>

        <!-- Split View (Chart + Map) -->
        <div class="split-view-container" v-if="viewMode === 'split'" ref="splitViewContainer">
          <!-- Empty State Overlay for Split -->
          <div class="split-empty-overlay" v-if="!hasData && !isLoading">
            <div class="empty-state-content">
              <i class="bi bi-layout-split icon-3x mb-3"></i>
              <h5>No Data Yet</h5>
              <p>Select a location, date range, and models, then click "Compare Models" to fetch data.</p>
            </div>
          </div>
          <div class="split-chart-section" :style="{ height: splitChartHeight + 'px', flex: 'none' }">
            <div class="comparison-chart" ref="splitChartContainer">
              <svg ref="splitChartSvg"></svg>
              <div class="chart-tooltip" ref="splitChartTooltip">
                <div class="tooltip-content"></div>
              </div>
            </div>
          </div>
          <div class="split-resize-handle" 
               @mousedown="startSplitResize"
               @touchstart.prevent="startSplitResize">
            <div class="resize-handle-bar"></div>
          </div>
          <div class="split-map-section">
            <div class="mc-map-wrap">
              <div class="mc-map" ref="splitMapEl"></div>
              <!-- Layer picker -->
              <div class="mc-map-layers mc-map-layers--compact">
                <button v-for="layer in mapLayerOptions" :key="layer.id" class="mc-map-layer-btn"
                  :class="{ 'mc-map-layer-btn--active': activeMapLayer === layer.id }" @click="switchMapLayer(layer.id)"
                  :title="layer.label">
                  <i :class="layer.icon"></i>
                </button>
              </div>
              <!-- Time controls -->
              <div class="mc-map-time mc-map-time--compact">
                <button class="mc-map-time-btn" @click="mapStepTime(-activeLayerInterval)" :disabled="mapIsPlaying">
                  <i class="bi bi-chevron-left"></i>
                </button>
                <button class="mc-map-time-btn" @click="mapPlayPause">
                  <i class="bi" :class="mapIsPlaying ? 'bi-pause-fill' : 'bi-play-fill'"></i>
                </button>
                <div class="mc-map-time-display">
                  <div class="mc-map-time-value">{{ mapTimeDisplay }}</div>
                </div>
                <button class="mc-map-time-btn" @click="mapStepTime(activeLayerInterval)" :disabled="mapIsPlaying">
                  <i class="bi bi-chevron-right"></i>
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Chart + Grid Split View -->
        <div class="chartgrid-view-container" v-if="viewMode === 'chartgrid'" ref="chartgridViewContainer">
          <!-- Empty State Overlay -->
          <div class="split-empty-overlay" v-if="!hasData && !isLoading">
            <div class="empty-state-content">
              <i class="bi bi-layout-text-window-reverse icon-3x mb-3"></i>
              <h5>No Data Yet</h5>
              <p>Select a location, date range, and models, then click "Compare Models" to fetch data.</p>
            </div>
          </div>
          <div class="chartgrid-chart-section" :style="{ height: chartgridChartHeight + 'px', flex: 'none' }">
            <div class="comparison-chart" ref="chartgridChartContainer">
              <svg ref="chartgridChartSvg"></svg>
              <div class="chart-tooltip" ref="chartgridChartTooltip">
                <div class="tooltip-content"></div>
              </div>
            </div>
            <!-- Chart Legend -->
            <div class="chart-legend chart-legend--compact" v-if="hasData">
              <div 
                v-for="model in selectedModelConfigs" 
                :key="model.id" 
                class="legend-item"
                :class="{ dimmed: highlightedModel && highlightedModel !== model.id }"
                @mouseenter="highlightModel(model.id)"
                @mouseleave="highlightModel(null)"
              >
                <span class="legend-color" :style="{ backgroundColor: model.color }"></span>
                <span class="legend-name">{{ model.name }}</span>
              </div>
            </div>
          </div>
          <div class="split-resize-handle" 
               @mousedown="startChartgridResize"
               @touchstart.prevent="startChartgridResize">
            <div class="resize-handle-bar"></div>
          </div>
          <div class="chartgrid-table-section">
            <table class="table table-sm table-striped table-hover">
              <thead>
                <tr>
                  <th class="sticky-col">Time</th>
                  <th 
                    v-for="model in selectedModelConfigs" 
                    :key="model.id"
                    :style="{ borderTop: `3px solid ${model.color}` }"
                    :class="{ 'legacy-model': hasV2Version(model.id), 'v2-model': isV2Model(model.id) }"
                  >
                    {{ model.name }}
                    <span v-if="hasV2Version(model.id)" class="th-legacy" title="Legacy - V2 version available">⚠️</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in tableData" :key="row.datetime">
                  <td class="sticky-col">{{ formatDateTime(row.datetime) }}</td>
                  <td 
                    v-for="model in selectedModelConfigs" 
                    :key="model.id"
                    :class="getCellClass(row, model.id)"
                  >
                    {{ formatValue(row[model.id]) }}
                    <div v-if="row._sources && row._sources[model.id]" class="cell-source" :title="row._sources[model.id]">{{ row._sources[model.id] }}</div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Empty State -->
        <div class="empty-state" v-if="!hasData && !isLoading && viewMode !== 'map' && viewMode !== 'split' && viewMode !== 'chartgrid'">
          <i class="bi bi-graph-up icon-3x mb-3"></i>
          <h5>No Data Yet</h5>
          <p>Select a location, date range, and models, then click "Compare Models" to fetch data.</p>
        </div>

        <!-- Loading State -->
        <div class="loading-state" v-if="isLoading">
          <i class="bi bi-arrow-repeat spin-icon icon-3x mb-3"></i>
          <h5>Loading Model Data...</h5>
          <p>Fetching from {{ selectedModels.length }} models</p>
          <div class="progress">
            <div class="progress-bar progress-bar-striped progress-bar-animated" :style="{ width: loadingProgress + '%' }"></div>
          </div>
        </div>
      </div>

      <!-- Stats Panel (Right) -->
      <div class="stats-panel" :class="{ collapsed: statsPanelCollapsed }">
        <div class="panel-header" @click="statsPanelCollapsed = !statsPanelCollapsed">
          <span>Statistics</span>
          <i class="bi" :class="statsPanelCollapsed ? 'bi-chevron-left' : 'bi-chevron-down'"></i>
        </div>
        
        <div class="panel-content" v-show="!statsPanelCollapsed && hasData">
          <!-- Stats Table -->
          <table class="stats-table">
            <thead>
              <tr>
                <th>Model</th>
                <th>Min</th>
                <th>Max</th>
                <th>Avg</th>
                <th>Time (ms)</th>
              </tr>
            </thead>
            <tbody>
              <tr 
                v-for="stat in modelStats" 
                :key="stat.id"
                :class="{ 'is-baseline': stat.id === baselineModel, 'is-highlighted': highlightedModel === stat.id, dimmed: highlightedModel && highlightedModel !== stat.id }"
                @mouseenter="highlightModel(stat.id)"
                @mouseleave="highlightModel(null)"
              >
                <td>
                  <span class="stat-color" :style="{ backgroundColor: stat.color }"></span>
                  {{ stat.name }}
                </td>
                <td :class="{ 'is-min': stat.isOverallMin }">{{ formatValue(stat.min) }}</td>
                <td :class="{ 'is-max': stat.isOverallMax }">{{ formatValue(stat.max) }}</td>
                <td>{{ formatValue(stat.avg) }}</td>
                <td class="response-time">{{ stat.responseTime || '' }}</td>
              </tr>
            </tbody>
          </table>

          <!-- Spread Analysis -->
          <div class="spread-analysis" v-if="spreadStats">
            <h6>Model Spread</h6>
            <div class="spread-item">
              <span>Range:</span>
              <strong>{{ formatValue(spreadStats.range) }}</strong>
            </div>
            <div class="spread-item">
              <span>Std Dev:</span>
              <strong>{{ formatValue(spreadStats.stdDev) }}</strong>
            </div>
            <div class="spread-item">
              <span>Agreement:</span>
              <strong :class="spreadStats.agreementClass">{{ spreadStats.agreement }}</strong>
            </div>
          </div>

          <!-- Forecast vs Obs Accuracy -->
          <div class="obs-accuracy" v-if="modelVsObsStats">
            <h6>Forecast vs Observations</h6>
            <table class="stats-table obs-accuracy-table">
              <thead>
                <tr>
                  <th>Model</th>
                  <th title="Mean Absolute Error">MAE</th>
                  <th title="Mean Bias">Bias</th>
                  <th title="Root Mean Square Error">RMSE</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="s in modelVsObsStats" :key="s.id" :class="{ 'is-best': s.isBest }">
                  <td>
                    <span class="stat-color" :style="{ backgroundColor: s.color }"></span>
                    {{ s.name }}
                  </td>
                  <td>{{ formatValue(s.mae) }}</td>
                  <td :class="s.bias >= 0 ? 'text-danger' : 'text-primary'">{{ (s.bias >= 0 ? '+' : '') + formatValue(s.bias) }}</td>
                  <td>{{ formatValue(s.rmse) }}</td>
                </tr>
              </tbody>
            </table>
            <div class="obs-accuracy-note">Based on {{ modelVsObsStats[0]?.n }} matched {{ timeResolution === 'minutes' ? 'minutes' : (timeResolution === 'hours' ? 'hours' : 'days') }}</div>
          </div>

          <!-- Attribution Info -->
          <div class="attribution-info" v-if="hasAttributionData">
            <h6>Model Attribution</h6>
            <div class="attribution-list">
              <div v-for="(attrs, modelId) in attributionData" :key="modelId" class="attribution-item">
                <div class="attribution-model">{{ getModelName(modelId) }}</div>
                <div v-for="attr in attrs" :key="attr.modelId" class="attribution-detail">
                  <span class="attr-model">{{ attr.modelId }}</span>
                  <span class="attr-run">{{ attr.runId }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Model Rankings -->
          <div class="model-rankings" v-if="baselineModel && modelRankings.length">
            <h6>Deviation from {{ getModelName(baselineModel) }}{{ hasV2Version(baselineModel) ? ' ⚠️' : '' }}</h6>
            <div 
              v-for="rank in modelRankings" 
              :key="rank.id" 
              class="ranking-item"
            >
              <span class="rank-color" :style="{ backgroundColor: rank.color }"></span>
              <span class="rank-name">{{ rank.name }}</span>
              <span class="rank-legacy" v-if="hasV2Version(rank.id)">⚠️</span>
              <span class="rank-v2" v-if="isV2Model(rank.id)">✓</span>
              <span class="rank-deviation" :class="rank.deviationClass">
                {{ rank.deviationText }}
              </span>
            </div>
          </div>

          <!-- API Query Links (CSV) -->
          <div class="query-links-section" v-if="modelQueryUrls.length > 0">
            <div class="query-links-header" @click="queryLinksExpanded = !queryLinksExpanded">
              <h6><i class="bi bi-link-45deg me-1"></i>API Queries (CSV)</h6>
              <i class="bi" :class="queryLinksExpanded ? 'bi-chevron-down' : 'bi-chevron-right'"></i>
            </div>
            <div class="query-links-list" v-show="queryLinksExpanded">
              <div v-for="q in modelQueryUrls" :key="q.modelId" class="query-link-item">
                <a :href="q.url" target="_blank" rel="noopener" :title="q.url">
                  <i class="bi bi-box-arrow-up-right me-1"></i>{{ q.name }}
                </a>
                <button class="btn btn-xs btn-copy-query" @click.prevent="copyQueryUrl(q.url)" title="Copy URL">
                  <i class="bi bi-clipboard"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Element Picker Modal -->
    <ElementPickerModal v-if="showElementPicker" v-model="elementsString"
      :unit-group="unitGroup"
      :defaultElements="defaultElementIds"
      @close="showElementPicker = false"
      @save="onElementsSelected" />

    <!-- Quick Compare Results Modal -->
    <div class="modal fade" :class="{ show: showQuickCompare }" v-if="showQuickCompare" @click.self="showQuickCompare = false">
      <div class="modal-dialog modal-xl">
        <div class="modal-content">
          <div class="modal-header qc-modal-header">
            <h5 class="modal-title"><i class="bi bi-lightning-charge me-2"></i>Quick Compare</h5>
            <button type="button" class="btn-close" @click="showQuickCompare = false"></button>
          </div>
          <div class="modal-body qc-modal-body" v-if="quickCompareResults">
            <!-- Summary bar -->
            <div class="qc-summary-bar">
              <div class="qc-summary-left">
                <span class="qc-summary-label">Baseline:</span>
                <strong>{{ getModelName(baselineModel) }}</strong>
                <span class="qc-summary-sep">&middot;</span>
                {{ quickCompareResults.modelCount }} {{ quickCompareResults.modelCount === 1 ? 'model' : 'models' }}
                <span class="qc-summary-sep">&middot;</span>
                {{ quickCompareResults.elementCount }} elements
              </div>
              <div class="qc-summary-counts">
                <span class="qc-count qc-count-missing" v-if="quickCompareResults.totalMissing > 0">{{ quickCompareResults.totalMissing }} missing</span>
                <span class="qc-count qc-count-partial" v-if="quickCompareResults.totalPartial > 0">{{ quickCompareResults.totalPartial }} gaps</span>
                <span class="qc-count qc-count-nointerp" v-if="quickCompareResults.totalNoInterp > 0">{{ quickCompareResults.totalNoInterp }} no-interp</span>
                <span class="qc-count qc-count-ok" v-if="quickCompareResults.totalMissing === 0 && quickCompareResults.totalPartial === 0 && quickCompareResults.totalNoInterp === 0">All OK</span>
              </div>
            </div>

            <!-- Filter bar -->
            <div class="qc-filter-bar">
              <span class="qc-filter-label">Show:</span>
              <button class="qc-filter-btn" :class="{ active: qcFilters.missing }" @click="qcFilters.missing = !qcFilters.missing">
                <i class="bi bi-x-circle-fill"></i> Missing
              </button>
              <button class="qc-filter-btn" :class="{ active: qcFilters.partial }" @click="qcFilters.partial = !qcFilters.partial">
                <i class="bi bi-exclamation-triangle-fill"></i> Gaps
              </button>
              <button class="qc-filter-btn" :class="{ active: qcFilters.nointerp }" @click="qcFilters.nointerp = !qcFilters.nointerp">
                <i class="bi bi-grid-3x3-gap"></i> No-interp
              </button>
              <button class="qc-filter-btn" :class="{ active: qcFilters.ok }" @click="qcFilters.ok = !qcFilters.ok">
                <i class="bi bi-check-circle-fill"></i> OK
              </button>
              <span class="qc-filter-count">{{ filteredQuickCompareRows.length }} / {{ quickCompareResults.rows.length }} elements</span>
            </div>

            <!-- Results table -->
            <div class="qc-table-wrap">
              <table class="table table-sm qc-table" v-if="filteredQuickCompareRows.length > 0">
                <thead>
                  <tr>
                    <th class="qc-th-element">Element</th>
                    <th v-for="model in quickCompareResults.models" :key="model.id" class="qc-th-model"
                        :style="{ borderTop: `3px solid ${model.color}` }">
                      {{ model.name }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in filteredQuickCompareRows" :key="row.elementId"
                      class="qc-row" :class="{ 'qc-row-issue': row.hasIssue }">
                    <td class="qc-cell-element">
                      <a href="#" @click.prevent="openQuickCompareChart(row.elementId)" class="qc-element-link">
                        {{ row.elementName }}
                      </a>
                    </td>
                    <td v-for="cell in row.cells" :key="cell.modelId"
                        class="qc-cell" :class="cell.cssClass"
                        :title="cell.tooltip">
                      <div class="qc-cell-content">
                        <div class="qc-cell-icons">
                          <span v-if="cell.completelyMissing" class="qc-icon-label qc-icon-missing"><i class="bi bi-x-circle-fill"></i> Missing</span>
                          <span v-if="cell.partiallyMissing" class="qc-icon-label qc-icon-partial"><i class="bi bi-exclamation-triangle-fill"></i> Gaps</span>
                          <span v-if="cell.notInterpolated" class="qc-icon-label qc-icon-nointerp"><i class="bi bi-grid-3x3-gap"></i> No-interp</span>
                        </div>
                        <div class="qc-cell-stats" v-if="!cell.completelyMissing">
                          <span class="qc-coverage" :class="cell.coverageClass">{{ cell.coverageText }}</span>
                          <span class="qc-spread" v-if="cell.spreadText" :class="cell.spreadClass">{{ cell.spreadText }}</span>
                        </div>
                        <div class="qc-cell-range" v-if="cell.shorterRange" :title="'Model covers ' + cell.rangePct + '% of baseline time range'">
                          <i class="bi bi-clock"></i> {{ cell.rangePct }}% range
                        </div>
                        <div class="qc-cell-missing-label" v-if="cell.completelyMissing">No data</div>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
              <div v-else class="qc-empty-filter">No elements match the selected filters.</div>
            </div>

            <!-- Legend -->
            <div class="qc-legend">
              <span class="qc-legend-item"><i class="bi bi-x-circle-fill qc-icon-missing"></i> Missing</span>
              <span class="qc-legend-item"><i class="bi bi-exclamation-triangle-fill qc-icon-partial"></i> Gaps in range</span>
              <span class="qc-legend-item"><i class="bi bi-grid-3x3-gap qc-icon-nointerp"></i> Non-interpolated &gt;20%</span>
              <span class="qc-legend-item"><i class="bi bi-clock qc-icon-range"></i> Shorter range</span>
              <span class="qc-legend-item"><span class="qc-spread-sample qc-spread-high">&plusmn;5.2</span> High</span>
              <span class="qc-legend-item"><span class="qc-spread-sample qc-spread-med">&plusmn;2.1</span> Medium</span>
              <span class="qc-legend-item"><span class="qc-spread-sample qc-spread-low">&plusmn;0.3</span> Low</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Help Modal -->
    <div class="modal fade" :class="{ show: showHelpModal }" v-if="showHelpModal" @click.self="showHelpModal = false">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Model Comparison Help</h5>
            <button type="button" class="btn-close" @click="showHelpModal = false"></button>
          </div>
          <div class="modal-body">
            <h6>Available Models</h6>
            <p>Compare forecasts from multiple weather models including:</p>
            <ul>
              <li><strong>Blended Results</strong> - Our optimized combined forecast</li>
              <li><strong>US Regional</strong> - HRRR, NAM, NDFD (higher resolution for US)</li>
              <li><strong>European</strong> - ECMWF, ICON-EU, ICON-D2, AROME, ARPEGE</li>
              <li><strong>Global</strong> - GFS, ICON Global, GDPS, UK Met Office</li>
              <li><strong>AI/ML</strong> - AI-powered models like ECMWF AIFS and AI GFS</li>
              <li><strong>Ensemble Models</strong> - GFS Ensemble (gfes_2) with 31 members showing forecast uncertainty</li>
            </ul>
            
            <h6>Ensemble Spread Visualization</h6>
            <p>When selecting ensemble models like <strong>GFS Ensemble (Spread)</strong>:</p>
            <ul>
              <li>A shaded area shows the range between minimum and maximum ensemble member values</li>
              <li>The center line shows the ensemble mean</li>
              <li>Toggle "Show Spread Range" in the Ensemble Models group to enable/disable</li>
              <li>Adjust opacity slider to change the spread area visibility</li>
              <li>Hover tooltips show both the mean and the full spread range</li>
            </ul>
            
            <h6>Comparison Views</h6>
            <ul>
              <li><strong>Chart</strong> - Line chart showing all selected models</li>
              <li><strong>Table</strong> - Tabular data with all values</li>
              <li><strong>Difference</strong> - Shows deviation from selected baseline</li>
            </ul>
            
            <h6>Tips</h6>
            <ul>
              <li>Hover over the chart to see values at specific times</li>
              <li>Click legend items to highlight individual models</li>
              <li>Use presets to quickly select model groups</li>
              <li>Select a baseline model to see relative differences</li>
              <li>Use the "Ensemble" preset to quickly compare ensemble forecasts</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
    </template>
  </div>
</template>

<script>
import * as d3 from 'd3';
import { getServerRoot } from './weatherexceptions/apiConfig.js';
import { VcGlobal } from '/src/visualcrossing-core/vcglobal_conn.js';
import { loadModelsFromApi } from '/src/data/weatherMapLayers.js';
import '/src/weather-map/WxTileLayer.js';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import ElementPickerModal from './queryapp/ElementPickerModal.vue';
import { ELEMENT_META, resolveUnit } from '@/data/elementInfoMeta';
import { getAllGroupsEnriched } from '@/data/elementInfo';

const MC_STORAGE_KEY = 'vc_model_comparison_config';

// Legacy models - these use the old forecastDataset parameter
// Note: Legacy models use prefixed IDs (legacy_xxx) to avoid conflicts with V2 models
// The apiModelId property stores the actual value to send to the API
const LEGACY_MODELS = [
  { id: 'default', name: 'Default', color: '#000000', group: 'blended', width: 3, noForecastDataset: true, isLegacy: false },
  { id: 'default_v2', name: 'Default V2', color: '#2E7D32', group: 'blended', width: 3, noForecastDataset: true, useV2Forecast: true, isLegacy: false },
  { id: 'default_v2_extended', name: 'Default V2 Extended', color: '#6A1B9A', group: 'blended', width: 3, noForecastDataset: true, useV2Forecast: true, useExtendedBlend: true, isLegacy: false },
  //{ id: 'vcglobalblended', apiModelId: 'vcglobalblended_2', name: 'VC Global Blended', color: '#1565C0', group: 'blended', width: 3, isLegacy: false },
  //{ id: 'vcglobalblended_conus', apiModelId: 'vcglobalblended_conus_2', name: 'VC Global Blended (CONUS)', color: '#E65100', group: 'blended', width: 3, isLegacy: false },
  { id: 'legacy_result', apiModelId: 'result', name: 'Result (Legacy)', color: '#000000', group: 'legacy', width: 4, isLegacy: true },
  { id: 'legacy_hrrr', apiModelId: 'hrrr', name: 'HRRR (Legacy)', color: '#0000FF', group: 'legacy', isLegacy: true },
  { id: 'legacy_nam', apiModelId: 'nam', name: 'NAM (Legacy)', color: '#FFFF00', group: 'legacy', isLegacy: true },
  { id: 'legacy_ndfd', apiModelId: 'ndfd', name: 'NDFD (Legacy)', color: '#FF00FF', group: 'legacy', isLegacy: true },
  { id: 'legacy_gfs', apiModelId: 'gfs', name: 'GFS (Legacy)', color: '#FF0000', group: 'legacy', isLegacy: true },
  { id: 'legacy_gfes', apiModelId: 'gfes', name: 'GFS Ensemble (Legacy)', color: '#CFCFCF', group: 'legacy', isLegacy: true },
  { id: 'legacy_usblended', apiModelId: 'usblended', name: 'US Blended (Legacy)', color: '#FFAAFF', group: 'legacy', isLegacy: true },
  { id: 'legacy_vcusblended', apiModelId: 'vcusblended', name: 'VC US Blended (Legacy)', color: '#00FFFF', group: 'legacy', width: 2, isLegacy: true },
  { id: 'legacy_icond2', apiModelId: 'icond2', name: 'ICON-D2 (Legacy)', color: '#AAFFAA', group: 'legacy', isLegacy: true },
  { id: 'legacy_iconeu', apiModelId: 'iconeu', name: 'ICON-EU (Legacy)', color: '#00FFFF', group: 'legacy', isLegacy: true },
  { id: 'legacy_ecmwf', apiModelId: 'ecmwf', name: 'ECMWF (Legacy)', color: '#00FF00', group: 'legacy', isLegacy: true },
  { id: 'legacy_iconglobal', apiModelId: 'iconglobal', name: 'ICON Global (Legacy)', color: '#FFAAFF', group: 'legacy', isLegacy: true },
  { id: 'legacy_gdps', apiModelId: 'gdps', name: 'GDPS (Legacy)', color: '#FFFFAA', group: 'legacy', isLegacy: true },
  { id: 'legacy_ukmo_global', apiModelId: 'ukmo_global', name: 'UK Met Global (Legacy)', color: '#FFFFAA', group: 'legacy', isLegacy: true },
];

// V2 model color palette (assigned dynamically when loaded from API)
const V2_COLORS = [
  '#2196F3', '#4CAF50', '#FF9800', '#9C27B0', '#00BCD4',
  '#E91E63', '#3F51B5', '#009688', '#FFC107', '#795548',
  '#607D8B', '#8BC34A', '#CDDC39', '#FF5722', '#673AB7',
  '#03A9F4', '#4DB6AC', '#FFB74D', '#BA68C8', '#81D4FA'
];

// Weather elements with display names
const ELEMENTS = [
  { id: 'datetime', name: 'Date/Time', hidden: true },
  { id: 'datetimeEpoch', name: 'Epoch', hidden: true },
  { id: 'icon', name: 'Icon', hidden: true },
  { id: 'source', name: 'Source', hidden: true },
  { id: 'temp', name: 'Temperature' },
  { id: 'tempmax', name: 'Temp Max', dailyOnly: true },
  { id: 'tempmin', name: 'Temp Min', dailyOnly: true },
  { id: 'feelslike', name: 'Feels Like' },
  { id: 'feelslikemax', name: 'Feels Like Max', dailyOnly: true },
  { id: 'feelslikemin', name: 'Feels Like Min', dailyOnly: true },
  { id: 'humidity', name: 'Humidity' },
  { id: 'dew', name: 'Dew Point' },
  { id: 'precip', name: 'Precipitation' },
  { id: 'precipprob', name: 'Precip Probability' },
  { id: 'snow', name: 'Snow' },
  { id: 'snowdepth', name: 'Snow Depth' },
  { id: 'windspeed', name: 'Wind Speed' },
  { id: 'windgust', name: 'Wind Gust' },
  { id: 'winddir', name: 'Wind Direction' },
  { id: 'pressure', name: 'Pressure' },
  { id: 'visibility', name: 'Visibility' },
  { id: 'cloudcover', name: 'Cloud Cover' },
  { id: 'uvindex', name: 'UV Index' },
  { id: 'solarradiation', name: 'Solar Radiation' },
  { id: 'solarenergy', name: 'Solar Energy' },
  { id: 'confidence', name: 'Confidence' },
  { id: 'aqius', name: 'AQI (US)' },
  { id: 'aqieur', name: 'AQI (EU)' },
  { id: 'pm2p5', name: 'PM2.5' },
  { id: 'pm10', name: 'PM10' },
  { id: 'pm1', name: 'PM1' },
  { id: 'o3', name: 'Ozone (O₃)' },
  { id: 'no2', name: 'NO₂' },
  { id: 'so2', name: 'SO₂' },
  { id: 'co', name: 'CO' },
  { id: 'smokesfc', name: 'Surface Smoke' },
  { id: 'smokecol', name: 'Smoke Column' },
  { id: 'dust', name: 'Dust' },
  { id: 'aod550', name: 'AOD (550nm)' },
  { id: 'hainesindex', name: 'Haines Index' },
  { id: 'stations', name: 'Stations', hidden: true },
  { id: 'spread', name: 'Spread', hidden: true },
];

// Map user-facing element names to spread sub-object field names
const ELEMENT_TO_SPREAD_KEY = {
  temp: 'temp', tempmax: 'temp', tempmin: 'temp',
  feelslike: 'temp', feelslikemax: 'temp', feelslikemin: 'temp',
  precip: 'precip', precipprob: 'precip',
  windspeed: 'windspeed', windgust: 'windspeed',
  winddir: 'wdir',
  pressure: 'sealevelpressure',
};

/** Get the spread value for an element from a record's spread object, or undefined */
function getSpreadValue(record, element) {
  if (!record || !record.spread) return undefined;
  const key = ELEMENT_TO_SPREAD_KEY[element];
  if (!key) return undefined;
  const v = record.spread[key];
  return (v !== undefined && v !== null && isFinite(v)) ? v : undefined;
}

/** Get the element value, handling special elements like confidence that live inside spread */
function getRecordValue(record, element) {
  if (!record) return undefined;
  if (element === 'confidence') return record.spread?.confidence;
  return record[element];
}

export default {
  name: 'ModelComparison',
  
  components: { ElementPickerModal },

  props: {
    apiKey: {
      type: String,
      default: ''
    },
    initialLocation: {
      type: Object,
      default: null
    }
  },

  data() {
    return {
      // Session/Auth State (populated from window.vcCommunicator)
      sessionId: null,
      sessionState: null,
      apiKeyOverride: '', // User-provided API key override
      serverRootOverride: null, // Component-level override (defers to global apiConfig setting)
      
      // Location - default to New York
      location: { lat: 40.7128, lon: -74.006, name: 'New York, NY' },
      locationText: 'New York, NY',
      isGeocoding: false,
      
      // Forecast basis date+time (null/empty = now/latest)
      forecastBasisDate: this.getDefaultForecastBasisDate(),
      forecastBasisTime: this.getDefaultForecastBasisTime(),
      forecastBasisUserSetTime: false,  // true once user manually changes the time
      forecastBasisTimezone: 'utc',  // 'utc' (default, backwards compatible) or 'local' (location timezone)
      
      // Date range
      startDate: this.getDefaultStartDate(),
      endDate: this.getDefaultEndDate(),
      
      // Options
      timeResolution: 'hours',
      minuteInterval: 15,  // Minute interval for minutely resolution (1, 5, 10, 15, 30)
      selectedElement: 'temp',
      baselineModel: 'default',
      viewMode: 'chartgrid',
      unitGroup: 'metric',  // metric, us, uk, base
      
      // Model selection
      selectedModels: ['default_v2'],
      expandedGroups: {
        combinations: true,
        v2: true,
        legacy: false
      },
      
      // V2 Models loaded from API
      v2Models: [],
      v2ModelsLoading: false,
      v2ModelsLoaded: false,
      
      // Ensemble spread options
      showEnsembleSpread: true,
      ensembleSpreadOpacity: 0.3,
      modelPanelCollapsed: false,
      statsPanelCollapsed: false,
      queryLinksExpanded: false,
      
      // API Options
      optionNointerpolate: true,
      optionAttribution: true,
      optionUsefcst: true,
      optionTrackModelChanges: false,
      optionCompareWithObs: false,
      
      // Data
      queryData: null,
      queryResponseTimes: {},  // { modelId: responseTimeMs }
      attributionData: {},  // Store attribution from each model query
      isLoading: false,
      loadingProgress: 0,
      
      // UI state
      showHelpModal: false,
      highlightedModel: null,
      
      // Lightweight map state
      leafletMap: null,
      splitLeafletMap: null,
      wxTileLayer: null,
      splitWxTileLayer: null,
      circleMarker: null,
      splitCircleMarker: null,
      activeMapLayer: 'temp',
      activeMapModel: '',
      activeLayerInterval: 60,
      mapDateTime: null,
      mapIsPlaying: false,
      mapPlayInterval: null,
      mapLayerOptions: [
        { id: 'precip', label: 'Precipitation', shortLabel: 'Rain', icon: 'bi bi-cloud-rain-fill', element: 'precipcomposite', interval: 10 },
        { id: 'temp', label: 'Temperature', shortLabel: 'Temp', icon: 'bi bi-thermometer-half', element: 'temp', interval: 60 },
        { id: 'wind', label: 'Wind', shortLabel: 'Wind', icon: 'bi bi-wind', element: 'wind', interval: 60 },
        { id: 'lightning', label: 'Lightning', shortLabel: 'Ltng', icon: 'bi bi-lightning-fill', element: 'lightning', interval: 10 },
        { id: 'cloud', label: 'Cloud Cover', shortLabel: 'Cloud', icon: 'bi bi-clouds-fill', element: 'cloudcover', interval: 60 },
        { id: 'pressure', label: 'Pressure', shortLabel: 'Pres', icon: 'bi bi-speedometer2', element: 'sealevelpressure', interval: 60 },
      ],
      
      // Model runs feature
      showModelRuns: false,  // Checkbox to enable model runs mode
      modelRunsLoading: false,
      modelRunsData: {},  // { modelId: { runs: [...], model: '...' } }
      selectedModelRuns: [],  // Array of { modelId, runId, displayName } objects
      expandedModelRuns: {},  // Track which models have expanded runs list
      
      // Split view resize
      splitChartHeight: 300,  // Initial chart height in pixels
      isResizingSplit: false,
      splitResizeStartY: 0,
      splitResizeStartHeight: 0,

      // Chart+Grid split view resize
      chartgridChartHeight: 280,
      isResizingChartgrid: false,
      chartgridResizeStartY: 0,
      chartgridResizeStartHeight: 0,
      
      // Track last fetch parameters to detect when refresh is needed
      lastFetchParams: null,

      // Quick Compare state
      showQuickCompare: false,
      quickCompareResults: null,
      quickCompareRunning: false,
      qcFilters: { missing: true, partial: true, nointerp: true, ok: true },

      // Element picker state
      showElementPicker: false,
      elementsString: '',  // comma-separated selected elements (empty = default ELEMENTS list)
      elementPickerFocusGroup: null,
    };
  },

  computed: {
    // Combine legacy models and V2 models loaded from API
    allModels() {
      return [...LEGACY_MODELS, ...this.v2Models];
    },
    
    modelGroups() {
      const groups = {
        v2: [],
        legacy: []
      };
      
      // Helper: extract the model family prefix for grouping
      // e.g., "GFS Ensemble" -> "GFS", "ICON-EU" -> "ICON", "HRRR Sub-Hourly" -> "HRRR"
      const getFamily = (name) => {
        if (!name) return '';
        // Split on space, hyphen-followed-by-uppercase, or parenthetical
        // Take the first word/segment as family
        const n = name.trim();
        const match = n.match(/^([\w]+)/);
        return match ? match[1].toUpperCase() : n.toUpperCase();
      };
      
      // Add V2 models, sorted alphabetically but grouped by model family
      this.v2Models.forEach(model => {
        groups.v2.push(model);
      });
      groups.v2.sort((a, b) => {
        const famA = getFamily(a.name || a.id);
        const famB = getFamily(b.name || b.id);
        if (famA !== famB) return famA.localeCompare(famB);
        return (a.name || a.id).localeCompare(b.name || b.id);
      });
      
      // Add legacy models, sorted alphabetically but grouped by model family
      LEGACY_MODELS.forEach(model => {
        if (model.isLegacy) {
          groups.legacy.push(model);
        }
      });
      groups.legacy.sort((a, b) => {
        const famA = getFamily(a.name || a.id);
        const famB = getFamily(b.name || b.id);
        if (famA !== famB) return famA.localeCompare(famB);
        return (a.name || a.id).localeCompare(b.name || b.id);
      });
      
      return groups;
    },
    
    modelsWithRuns() {
      // Return V2 models that have available runs data, sorted alphabetically
      return this.v2Models
        .filter(model => this.hasAvailableRuns(model.id))
        .sort((a, b) => (a.name || a.id).localeCompare(b.name || b.id));
    },
    
    hasEnsembleModel() {
      return this.selectedModels.some(id => {
        const model = this.allModels.find(m => m.id === id);
        return model && model.isEnsemble;
      });
    },
    
    // Server root URL - uses centralized apiConfig with optional component override
    serverRoot() {
      return getServerRoot(this.serverRootOverride);
    },
    
    // Location key for forcing re-render of model bounds checks
    locationKey() {
      if (!this.location) return 'none';
      return `${this.location.lat.toFixed(2)}_${this.location.lon.toFixed(2)}`;
    },
    
    selectedModelCount() {
      return this.selectedModels.length;
    },
    
    totalModelCount() {
      return this.allModels.length;
    },
    
    selectedModelConfigs() {
      const configs = this.selectedModels.map(id => 
        this.allModels.find(m => m.id === id)
      ).filter(Boolean);
      
      // Add run configs from queryData if available
      if (this.showModelRuns && this.queryData) {
        Object.keys(this.queryData).forEach(key => {
          const data = this.queryData[key];
          if (data && data._runDisplayConfig) {
            configs.push(data._runDisplayConfig);
          }
        });
      }
      
      // Add observations entry when compare-with-obs data is present
      if (this.queryData && this.queryData['_observations']) {
        configs.push({
          id: '_observations',
          name: 'Actual Observations',
          color: '#E91E63',
          width: 3,
          dashStyle: 'solid',
          isObservation: true
        });
      }
      
      return configs;
    },
    
    /** The default list of element IDs (from the hardcoded ELEMENTS array) */
    defaultElementIds() {
      return ELEMENTS.filter(e => !e.hidden).map(e => e.id);
    },

    /** True when the user has not customized elements (elementsString is empty) */
    isDefaultElements() {
      return !this.elementsString || !this.elementsString.trim();
    },

    /** IDs the user has chosen via the element picker (or default ELEMENTS) */
    selectedElementIds() {
      const REQUIRED = ['datetime', 'datetimeEpoch', 'source'];
      if (!this.elementsString || !this.elementsString.trim()) {
        return ELEMENTS.map(e => e.id);
      }
      const tokens = this.elementsString.split(',').map(s => s.trim()).filter(Boolean);
      const hasDelta = tokens.some(t => /^(add|remove):/i.test(t));
      let ids;
      if (!hasDelta) {
        // Explicit list of element IDs
        ids = tokens;
      } else {
        // Delta mode: start from defaults, apply add:/remove: tokens
        const result = new Set(ELEMENTS.map(e => e.id));
        for (const t of tokens) {
          const m = t.match(/^(add|remove):(.+)$/i);
          if (!m) continue;
          const op = m[1].toLowerCase();
          const id = m[2].trim();
          if (op === 'add') result.add(id);
          else if (op === 'remove') result.delete(id);
        }
        ids = Array.from(result);
      }
      // Always ensure datetime and datetimeEpoch are included
      for (const req of REQUIRED) {
        if (!ids.includes(req)) ids.unshift(req);
      }
      return ids;
    },

    /**
     * Build the dropdown list from:
     *  1. The user's selected elements (from the picker)
     *  2. Plus any extra element keys discovered in fetched API data
     * Each entry has { id, name }.
     */
    availableElements() {
      // Start with the selected element IDs as a base
      const elementMap = new Map();

      // Add user-selected elements first
      for (const id of this.selectedElementIds) {
        const staticEl = ELEMENTS.find(e => e.id === id);
        const meta = ELEMENT_META[id];
        const name = staticEl?.name || meta?.label || this.humanizeElementId(id);
        const hidden = staticEl?.hidden || false;
        const dailyOnly = staticEl?.dailyOnly || (meta?.sections && meta.sections.length === 1 && meta.sections[0] === 'days');
        elementMap.set(id, { id, name, hidden, dailyOnly });
      }

      // Discover additional element keys from fetched data
      if (this.queryData) {
        for (const modelData of Object.values(this.queryData)) {
          if (!modelData?.days) continue;
          for (const day of modelData.days) {
            for (const key of Object.keys(day)) {
              if (!elementMap.has(key) && key !== 'hours' && key !== 'source' && key !== 'stations' && key !== 'spread') {
                const meta = ELEMENT_META[key];
                const name = meta?.label || this.humanizeElementId(key);
                elementMap.set(key, { id: key, name, hidden: false, dailyOnly: false });
              }
            }
            // Also scan hourly records
            if (day.hours) {
              for (const hour of day.hours) {
                for (const key of Object.keys(hour)) {
                  if (!elementMap.has(key) && key !== 'source' && key !== 'stations' && key !== 'spread' && key !== 'minutes') {
                    const meta = ELEMENT_META[key];
                    const name = meta?.label || this.humanizeElementId(key);
                    elementMap.set(key, { id: key, name, hidden: false, dailyOnly: false });
                  }
                }
                // Also scan minute records
                if (hour.minutes) {
                  for (const min of hour.minutes) {
                    for (const key of Object.keys(min)) {
                      if (!elementMap.has(key) && key !== 'source' && key !== 'stations' && key !== 'spread') {
                        const meta = ELEMENT_META[key];
                        const name = meta?.label || this.humanizeElementId(key);
                        elementMap.set(key, { id: key, name, hidden: false, dailyOnly: false });
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }

      // Filter: hide hidden, hide dailyOnly when hourly
      return Array.from(elementMap.values()).filter(el => {
        if (el.hidden) return false;
        if (el.dailyOnly && this.timeResolution !== 'days') return false;
        return true;
      });
    },
    
    /**
     * Get the effective API key to use for API requests
     * Priority: 1) Prop apiKey, 2) User override, 3) Session key, 4) window.vcApiKey
     */
    weatherApiKey() {
      // First check prop
      if (this.apiKey && this.apiKey.trim()) {
        return this.apiKey.trim();
      }
      // Then user override
      if (this.apiKeyOverride && this.apiKeyOverride.trim()) {
        return this.apiKeyOverride.trim();
      }
      // Then session state
      if (this.sessionState?.apiKey) {
        return this.sessionState.apiKey;
      }
      // Finally window global
      return window.vcApiKey || '';
    },
    
    hasValidApiKey() {
      return !!this.weatherApiKey;
    },
    
    hasSession() {
      return !!this.sessionState?.apiKey;
    },
    
    canFetch() {
      const hasModelsOrRuns = this.selectedModels.length > 0 || (this.showModelRuns && this.selectedModelRuns.length > 0);
      return this.location && hasModelsOrRuns && this.startDate && this.endDate && this.hasValidApiKey;
    },
    
    /** Combined forecastBasisDateTime from separate date + time fields */
    forecastBasisDateTime() {
      if (!this.forecastBasisDate) return null;
      const time = this.forecastBasisTime || '00:00';
      return `${this.forecastBasisDate}T${time}`;
    },

    /** True when the forecast basis is "now" (latest available models) */
    isForecastBasisNow() {
      if (!this.forecastBasisDate) return true;
      // Compare with current time rounded to last hour - if within ~5 min, treat as "now"
      const basisTime = new Date(this.forecastBasisDateTime);
      const now = new Date();
      now.setMinutes(0, 0, 0);
      return Math.abs(basisTime.getTime() - now.getTime()) < 5 * 60 * 1000;
    },
    
    /** Human-readable display of the forecast basis datetime */
    formatForecastBasis() {
      if (!this.forecastBasisDate) return 'now';
      const d = new Date(this.forecastBasisDateTime);
      return d.toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    },
    
    // Current fetch parameters - used to detect if settings have changed
    currentFetchParams() {
      return {
        location: this.location ? `${this.location.lat},${this.location.lon}` : null,
        startDate: this.startDate,
        endDate: this.endDate,
        showModelRuns: this.showModelRuns,
        selectedModels: this.showModelRuns ? [] : [...this.selectedModels].sort(),
        selectedModelRuns: this.showModelRuns ? this.selectedModelRuns.map(r => `${r.modelId}:${r.runId}`).sort() : [],
        timeResolution: this.timeResolution,
        minuteInterval: this.minuteInterval,
        unitGroup: this.unitGroup,
        optionUsefcst: this.optionUsefcst,
        optionNointerpolate: this.optionNointerpolate,
        optionAttribution: this.optionAttribution,
        optionTrackModelChanges: this.optionTrackModelChanges,
        forecastBasisDateTime: this.forecastBasisDateTime,
        forecastBasisTimezone: this.forecastBasisTimezone,
        optionCompareWithObs: this.optionCompareWithObs,
        elementsString: this.elementsString,
      };
    },
    
    // Check if current settings differ from last fetch
    needsRefresh() {
      if (!this.hasData || !this.lastFetchParams) return false;
      const current = this.currentFetchParams;
      const last = this.lastFetchParams;
      return JSON.stringify(current) !== JSON.stringify(last);
    },
    
    hasData() {
      return this.queryData && Object.keys(this.queryData).length > 0;
    },
    
    hasAttributionData() {
      return this.attributionData && Object.keys(this.attributionData).length > 0;
    },

    /**
     * Build CSV query URLs for each fetched model so users can open them directly.
     * Returns an array of { modelId, name, url } objects.
     */
    modelQueryUrls() {
      if (!this.hasData) return [];

      const apiKey = this.weatherApiKey;
      if (!apiKey) return [];

      const baseUrl = this.serverRoot + '/VisualCrossingWebServices/rest/services/timeline';
      const locationStr = this.locationText.trim() || `${this.location.lat},${this.location.lon}`;
      const include = this.timeResolution === 'minutes' ? 'minutes' : (this.timeResolution === 'hours' ? 'hours' : 'days');
      const forecastBasisParam = this.getForecastBasisParam();
      const elementsStr = this.isDefaultElements ? '' : this.selectedElementIds.join(',');
      const elementsPart = elementsStr ? `&elements=${encodeURIComponent(elementsStr)}` : '';

      const urls = [];

      for (const key of Object.keys(this.queryData)) {
        if (key === '_observations') {
          // Build observation query URL
          const obsOptionsList = ['useobs'];
          if (this.timeResolution === 'minutes' && this.minuteInterval) {
            obsOptionsList.push(`minuteinterval_${this.minuteInterval}`);
          }
          let url = `${baseUrl}/${encodeURIComponent(locationStr)}/${this.startDate}/${this.endDate}?unitGroup=${this.unitGroup}${elementsPart}&include=${include}&key=${apiKey}&options=${encodeURIComponent(obsOptionsList.join(','))}&contentType=csv`;
          urls.push({ modelId: key, name: 'Actual Observations', url });
          continue;
        }

        // Find model config
        // key may be a plain modelId or a run key like "modelId_run_runId"
        const isRun = key.includes('_run_');
        const baseModelId = isRun ? key.split('_run_')[0] : key;
        const runId = isRun ? key.split('_run_')[1] : null;
        const modelConfig = this.allModels.find(m => m.id === baseModelId);
        const isV2Model = !!modelConfig?.useV2Forecast;

        // Build options string mirroring fetchData logic
        const optionsList = [];
        if (this.timeResolution === 'minutes' && this.minuteInterval) {
          optionsList.push(`minuteinterval_${this.minuteInterval}`);
        }
        if (!forecastBasisParam && this.optionUsefcst) optionsList.push('usefcst');
        if (forecastBasisParam && isV2Model && this.forecastBasisTimezone === 'local') optionsList.push('forecastbasisdatelocal');
        if (this.optionNointerpolate) optionsList.push('nointerpolate');
        if (this.optionAttribution) optionsList.push('attribution');
        if (isV2Model) optionsList.push('usev2forecast');
        if (this.optionTrackModelChanges && (baseModelId === 'default' || baseModelId === 'default_v2' || baseModelId === 'default_v2_extended')) {
          optionsList.push('trackmodelchanges');
        }
        if (modelConfig?.useExtendedBlend) optionsList.push('useextendedblend');

        // Build forecastDataset param
        let forecastDataset = null;
        if (baseModelId && !(modelConfig && modelConfig.noForecastDataset)) {
          const runData = this.queryData[key]?._runDisplayConfig;
          forecastDataset = (runData && runData.isRun) ? (modelConfig?.apiModelId || baseModelId) : (modelConfig?.apiModelId || baseModelId);
          if (runId) {
            forecastDataset = `${forecastDataset}:${runId}`;
          }
        }

        let url = `${baseUrl}/${encodeURIComponent(locationStr)}/${this.startDate}/${this.endDate}?unitGroup=${this.unitGroup}${elementsPart}&include=${include}&key=${apiKey}`;
        if (forecastDataset) url += `&forecastDataset=${encodeURIComponent(forecastDataset)}`;
        if (optionsList.length) url += `&options=${encodeURIComponent(optionsList.join(','))}`;
        if (forecastBasisParam) {
          const fbdValue = isV2Model ? forecastBasisParam : forecastBasisParam.substring(0, 10);
          url += `&forecastBasisDate=${encodeURIComponent(fbdValue)}`;
        }
        url += '&contentType=csv';

        // Determine display name
        let name;
        const runDisplayConfig = this.queryData[key]?._runDisplayConfig;
        if (runDisplayConfig) {
          name = runDisplayConfig.name;
        } else {
          name = modelConfig?.name || key;
        }

        urls.push({ modelId: key, name, url });
      }

      return urls;
    },

    // Map time display
    mapTimeDisplay() {
      if (!this.mapDateTime) return '--';
      return this.mapDateTime.toLocaleTimeString(undefined, { hour: 'numeric' });
    },
    
    mapDateDisplay() {
      if (!this.mapDateTime) return '';
      return this.mapDateTime.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    },
    
    // Build list of model options for the map model picker dropdown
    mapModelOptions() {
      const options = [{ id: '', name: 'Default (hybrid)' }];
      this.selectedModels.forEach(modelId => {
        const model = this.allModels.find(m => m.id === modelId);
        if (model && !model.isLegacy) {
          const apiId = model.apiModelId || model.model || model.id;
          options.push({ id: apiId, name: model.name });
        }
      });
      return options;
    },

    tableData() {
      if (!this.hasData) return [];
      
      const data = [];
      const element = this.selectedElement;
      
      // Collect all unique datetimes from all model configs (including runs)
      const datetimes = new Set();
      this.selectedModelConfigs.forEach(config => {
        if (!config) return;
        const modelData = this.queryData[config.id];
        if (modelData && modelData.days) {
          modelData.days.forEach(day => {
            if (this.timeResolution === 'minutes' && day.hours) {
              day.hours.forEach(hour => {
                if (hour.minutes) {
                  hour.minutes.forEach(min => datetimes.add(min.datetimeEpoch));
                }
              });
            } else if (this.timeResolution === 'hours' && day.hours) {
              day.hours.forEach(hour => datetimes.add(hour.datetimeEpoch));
            } else {
              datetimes.add(day.datetimeEpoch);
            }
          });
        }
      });
      
      // Sort datetimes
      const sortedDatetimes = Array.from(datetimes).sort((a, b) => a - b);
      
      // Build rows
      sortedDatetimes.forEach(dt => {
        const row = { datetime: dt };
        const sources = {};
        let hasAnyValue = false;
        
        this.selectedModelConfigs.forEach(config => {
          if (!config) return;
          const modelData = this.queryData[config.id];
          if (modelData && modelData.days) {
            const value = this.getValueAtTime(modelData, dt, element);
            row[config.id] = value;
            if (value !== null && value !== undefined) {
              hasAnyValue = true;
            }
            // Collect source info if trackModelChanges is enabled
            if (this.optionTrackModelChanges) {
              const source = this.getSourceAtTime(modelData, dt, element);
              if (source) sources[config.id] = source;
            }
          }
        });
        
        // Attach sources to row if any exist
        if (Object.keys(sources).length > 0) {
          row._sources = sources;
        }
        
        data.push(row);
      });
      
      return data;
    },
    
    modelStats() {
      if (!this.hasData) return [];
      
      const element = this.selectedElement;
      const stats = [];
      
      let overallMin = Infinity;
      let overallMax = -Infinity;
      let minModelId = null;
      let maxModelId = null;
      
      this.selectedModelConfigs.forEach(config => {
        if (!config) return;
        const modelId = config.id;
        const modelData = this.queryData[modelId];
        if (!modelData || !modelData.days) return;
        
        const values = this.extractValues(modelData, element);
        if (values.length === 0) return;
        
        const min = Math.min(...values);
        const max = Math.max(...values);
        const avg = values.reduce((a, b) => a + b, 0) / values.length;
        
        if (min < overallMin) {
          overallMin = min;
          minModelId = modelId;
        }
        if (max > overallMax) {
          overallMax = max;
          maxModelId = modelId;
        }
        
        stats.push({
          id: modelId,
          name: config.name || modelId,
          color: config.color || '#888',
          min,
          max,
          avg,
          responseTime: this.queryResponseTimes[modelId] || null
        });
      });
      
      // Mark overall min/max
      stats.forEach(s => {
        s.isOverallMin = s.id === minModelId;
        s.isOverallMax = s.id === maxModelId;
      });
      
      return stats;
    },
    
    spreadStats() {
      if (this.modelStats.length < 2) return null;
      
      const avgs = this.modelStats.map(s => s.avg);
      const min = Math.min(...avgs);
      const max = Math.max(...avgs);
      const range = max - min;
      
      const mean = avgs.reduce((a, b) => a + b, 0) / avgs.length;
      const variance = avgs.reduce((a, v) => a + Math.pow(v - mean, 2), 0) / avgs.length;
      const stdDev = Math.sqrt(variance);
      
      let agreement = 'Low';
      let agreementClass = 'text-danger';
      
      if (stdDev < 2) {
        agreement = 'High';
        agreementClass = 'text-success';
      } else if (stdDev < 5) {
        agreement = 'Moderate';
        agreementClass = 'text-warning';
      }
      
      return { range, stdDev, agreement, agreementClass };
    },
    
    canQuickCompare() {
      return this.baselineModel && this.hasData && this.selectedModelConfigs.length > 1 && !this.quickCompareRunning;
    },
    filteredQuickCompareRows() {
      if (!this.quickCompareResults) return [];
      return this.quickCompareResults.rows.filter(row => {
        const hasMissing = row.cells.some(c => c.completelyMissing);
        const hasPartial = row.cells.some(c => c.partiallyMissing);
        const hasNoInterp = row.cells.some(c => c.notInterpolated);
        const isOk = !hasMissing && !hasPartial && !hasNoInterp;
        if (hasMissing && this.qcFilters.missing) return true;
        if (hasPartial && this.qcFilters.partial) return true;
        if (hasNoInterp && this.qcFilters.nointerp) return true;
        if (isOk && this.qcFilters.ok) return true;
        return false;
      });
    },

    modelRankings() {
      if (!this.baselineModel || !this.hasData) return [];
      
      const baselineStat = this.modelStats.find(s => s.id === this.baselineModel);
      if (!baselineStat) return [];
      
      return this.modelStats
        .filter(s => s.id !== this.baselineModel)
        .map(s => {
          const deviation = s.avg - baselineStat.avg;
          const model = this.allModels.find(m => m.id === s.id);
          
          return {
            id: s.id,
            name: model?.name || s.id,
            color: model?.color || '#888',
            deviation,
            deviationText: deviation >= 0 ? `+${this.formatValue(deviation)}` : this.formatValue(deviation),
            deviationClass: deviation >= 0 ? 'text-danger' : 'text-primary'
          };
        })
        .sort((a, b) => Math.abs(b.deviation) - Math.abs(a.deviation));
    },

    /** Compute MAE, Bias, RMSE for every model vs actual observations */
    modelVsObsStats() {
      const obsData = this.queryData?.['_observations'];
      if (!obsData || !obsData.days) return null;
      if (!this.hasData) return null;

      const element = this.selectedElement;

      // Build obs lookup: epoch -> value
      const obsMap = new Map();
      obsData.days.forEach(day => {
        if (this.timeResolution === 'minutes' && day.hours) {
          day.hours.forEach(h => {
            if (h.minutes) {
              h.minutes.forEach(m => {
                const v = getRecordValue(m, element);
                if (v !== undefined && v !== null) obsMap.set(m.datetimeEpoch, v);
              });
            }
          });
        } else if (this.timeResolution === 'hours' && day.hours) {
          day.hours.forEach(h => {
            const v = getRecordValue(h, element);
            if (v !== undefined && v !== null) obsMap.set(h.datetimeEpoch, v);
          });
        } else {
          const v = getRecordValue(day, element);
          if (v !== undefined && v !== null) obsMap.set(day.datetimeEpoch, v);
        }
      });

      if (obsMap.size === 0) return null;

      const results = [];

      this.selectedModelConfigs.forEach(config => {
        if (!config || config.id === '_observations') return;
        const modelId = config.id;
        const modelData = this.queryData[modelId];
        if (!modelData || !modelData.days) return;

        let sumErr = 0, sumAbsErr = 0, sumSqErr = 0, n = 0;

        modelData.days.forEach(day => {
          if (this.timeResolution === 'minutes' && day.hours) {
            day.hours.forEach(h => {
              if (h.minutes) {
                h.minutes.forEach(m => {
                  const mv = getRecordValue(m, element);
                  const ov = obsMap.get(m.datetimeEpoch);
                  if (mv !== undefined && mv !== null && ov !== undefined) {
                    const err = mv - ov;
                    sumErr += err;
                    sumAbsErr += Math.abs(err);
                    sumSqErr += err * err;
                    n++;
                  }
                });
              }
            });
          } else if (this.timeResolution === 'hours' && day.hours) {
            day.hours.forEach(h => {
              const mv = getRecordValue(h, element);
              const ov = obsMap.get(h.datetimeEpoch);
              if (mv !== undefined && mv !== null && ov !== undefined) {
                const err = mv - ov;
                sumErr += err;
                sumAbsErr += Math.abs(err);
                sumSqErr += err * err;
                n++;
              }
            });
          } else {
            const mv = getRecordValue(day, element);
            const ov = obsMap.get(day.datetimeEpoch);
            if (mv !== undefined && mv !== null && ov !== undefined) {
              const err = mv - ov;
              sumErr += err;
              sumAbsErr += Math.abs(err);
              sumSqErr += err * err;
              n++;
            }
          }
        });

        if (n > 0) {
          results.push({
            id: modelId,
            name: config.name || modelId,
            color: config.color || '#888',
            n,
            bias: sumErr / n,
            mae: sumAbsErr / n,
            rmse: Math.sqrt(sumSqErr / n)
          });
        }
      });

      if (results.length === 0) return null;

      // Sort by MAE ascending (best first)
      results.sort((a, b) => a.mae - b.mae);
      // Mark the best (lowest MAE)
      if (results.length > 1) results[0].isBest = true;
      return results;
    }
  },

  methods: {
    // ---- Persist/Restore Configuration ----
    saveConfig() {
      try {
        const config = {
          selectedModels: this.selectedModels,
          selectedElement: this.selectedElement,
          elementsString: this.elementsString,
          timeResolution: this.timeResolution,
          minuteInterval: this.minuteInterval,
          unitGroup: this.unitGroup,
          viewMode: this.viewMode,
          baselineModel: this.baselineModel,
          locationText: this.locationText,
          optionNointerpolate: this.optionNointerpolate,
          optionAttribution: this.optionAttribution,
          optionUsefcst: this.optionUsefcst,
          optionTrackModelChanges: this.optionTrackModelChanges,
          optionCompareWithObs: this.optionCompareWithObs,
          expandedGroups: this.expandedGroups,
          modelPanelCollapsed: this.modelPanelCollapsed,
          statsPanelCollapsed: this.statsPanelCollapsed,
        };
        localStorage.setItem(MC_STORAGE_KEY, JSON.stringify(config));
      } catch (e) { /* ignore quota errors */ }
    },
    restoreConfig() {
      try {
        const raw = localStorage.getItem(MC_STORAGE_KEY);
        if (!raw) return;
        const config = JSON.parse(raw);
        if (config.selectedModels) this.selectedModels = config.selectedModels;
        if (config.selectedElement) this.selectedElement = config.selectedElement;
        if (config.elementsString !== undefined) this.elementsString = config.elementsString;
        if (config.timeResolution) this.timeResolution = config.timeResolution;
        if (config.minuteInterval) this.minuteInterval = config.minuteInterval;
        if (config.unitGroup) this.unitGroup = config.unitGroup;
        if (config.viewMode) this.viewMode = config.viewMode;
        if (config.baselineModel !== undefined) this.baselineModel = config.baselineModel;
        if (config.locationText) this.locationText = config.locationText;
        if (config.optionNointerpolate !== undefined) this.optionNointerpolate = config.optionNointerpolate;
        if (config.optionAttribution !== undefined) this.optionAttribution = config.optionAttribution;
        if (config.optionUsefcst !== undefined) this.optionUsefcst = config.optionUsefcst;
        if (config.optionTrackModelChanges !== undefined) this.optionTrackModelChanges = config.optionTrackModelChanges;
        if (config.optionCompareWithObs !== undefined) this.optionCompareWithObs = config.optionCompareWithObs;
        if (config.expandedGroups) this.expandedGroups = config.expandedGroups;
        if (config.modelPanelCollapsed !== undefined) this.modelPanelCollapsed = config.modelPanelCollapsed;
        if (config.statsPanelCollapsed !== undefined) this.statsPanelCollapsed = config.statsPanelCollapsed;
      } catch (e) { /* ignore parse errors */ }
    },

    // ---- Lightweight Leaflet Map ----
    initMap(mapEl) {
      if (!mapEl || !L) return null;
      
      const lat = this.location ? this.location.lat : 40.7;
      const lon = this.location ? this.location.lon : -74.0;
      
      const map = new L.Map(mapEl, {
        fadeAnimation: false,
        minZoom: 1,
        maxZoom: 16,
        center: [lat, lon],
        zoom: 8,
        zoomControl: false,
        attributionControl: false,
      });
      
      L.control.zoom({ position: 'topright' }).addTo(map);
      L.control.attribution({ position: 'bottomright', prefix: false }).addTo(map);
      
      // Base tiles
      L.tileLayer('https://{s}.basemaps.cartocdn.com/light_nolabels/{z}/{x}/{y}{r}.png', {
        subdomains: 'abcd', maxZoom: 20,
      }).addTo(map);
      
      // Resize observer
      const ro = new ResizeObserver(() => map.invalidateSize());
      ro.observe(mapEl);
      
      // Initialize time if needed
      if (!this.mapDateTime) {
        this.mapDateTime = this.roundToInterval(new Date(), this.activeLayerInterval);
      }
      map.options.dateTime = this.mapDateTime;
      map.options.dateTimeInterval = this.activeLayerInterval;
      
      return map;
    },
    
    createWxLayer(model) {
      const config = this.mapLayerOptions.find(l => l.id === this.activeMapLayer) || this.mapLayerOptions[0];
      return new L.TileLayer.WxTileLayer({
        opacity: 0.65,
        level: 0,
        model: model || null,
        element: config.element,
        forceRefresh: true,
        key: VcGlobal.MAP_TILE_KEY,
        unitGroup: this.unitGroup,
        dateTimeInterval: config.interval,
      });
    },
    
    initializeMapView() {
      this.$nextTick(() => {
        // Sync active layer to current element before creating tiles
        this.activeMapLayer = this.elementToMapLayer(this.selectedElement);
        const config = this.mapLayerOptions.find(l => l.id === this.activeMapLayer);
        if (config) this.activeLayerInterval = config.interval;
        
        // Primary map
        if (this.viewMode === 'map' && this.$refs.mapEl && !this.leafletMap) {
          this.leafletMap = this.initMap(this.$refs.mapEl);
          if (this.leafletMap) {
            this.wxTileLayer = this.createWxLayer(this.activeMapModel || null);
            this.leafletMap.addLayer(this.wxTileLayer);
            this.updateMapMarker(this.leafletMap, 'circleMarker');
          }
        }
        // Split map
        if (this.viewMode === 'split' && this.$refs.splitMapEl && !this.splitLeafletMap) {
          this.splitLeafletMap = this.initMap(this.$refs.splitMapEl);
          if (this.splitLeafletMap) {
            this.splitWxTileLayer = this.createWxLayer(this.activeMapModel || null);
            this.splitLeafletMap.addLayer(this.splitWxTileLayer);
            this.updateMapMarker(this.splitLeafletMap, 'splitCircleMarker');
          }
        }
        // Sync location if maps already exist
        this.syncMapLocation();
      });
    },
    
    updateMapMarker(map, markerProp) {
      if (!map || !this.location) return;
      if (this[markerProp]) this[markerProp].remove();
      this[markerProp] = L.circleMarker([this.location.lat, this.location.lon], {
        color: '#3b82f6', fillColor: '#3b82f6', fillOpacity: 0.6, radius: 5, weight: 2,
      }).addTo(map);
    },
    
    syncMapLocation() {
      if (!this.location) return;
      const coords = [this.location.lat, this.location.lon];
      if (this.leafletMap) {
        const z = Math.max(this.leafletMap.getZoom(), 7);
        this.leafletMap.setView(coords, z);
        this.updateMapMarker(this.leafletMap, 'circleMarker');
      }
      if (this.splitLeafletMap) {
        const z = Math.max(this.splitLeafletMap.getZoom(), 7);
        this.splitLeafletMap.setView(coords, z);
        this.updateMapMarker(this.splitLeafletMap, 'splitCircleMarker');
      }
    },
    
    switchMapLayer(layerId) {
      if (this.activeMapLayer === layerId) return;
      this.activeMapLayer = layerId;
      const config = this.mapLayerOptions.find(l => l.id === layerId);
      if (!config) return;
      this.activeLayerInterval = config.interval;
      
      // Replace WxTileLayer on each active map
      const replaceLayer = (map, layerProp) => {
        if (!map) return;
        if (this[layerProp]) map.removeLayer(this[layerProp]);
        this[layerProp] = this.createWxLayer(this.activeMapModel || null);
        map.addLayer(this[layerProp]);
      };
      replaceLayer(this.leafletMap, 'wxTileLayer');
      replaceLayer(this.splitLeafletMap, 'splitWxTileLayer');
      
      if (this.mapDateTime) this.setMapDateTime(this.mapDateTime);
    },
    
    switchMapModel() {
      // activeMapModel is already bound via v-model
      const replaceLayer = (map, layerProp) => {
        if (!map) return;
        if (this[layerProp]) map.removeLayer(this[layerProp]);
        this[layerProp] = this.createWxLayer(this.activeMapModel || null);
        map.addLayer(this[layerProp]);
      };
      replaceLayer(this.leafletMap, 'wxTileLayer');
      replaceLayer(this.splitLeafletMap, 'splitWxTileLayer');
      if (this.mapDateTime) this.setMapDateTime(this.mapDateTime);
    },
    
    // ---- Map time controls ----
    roundToInterval(datetime, intervalMinutes) {
      const ms = datetime.getTime();
      const intervalMs = intervalMinutes * 60 * 1000;
      return new Date(Math.floor(ms / intervalMs) * intervalMs);
    },
    
    setMapDateTime(datetime) {
      const rounded = this.roundToInterval(datetime, this.activeLayerInterval);
      this.mapDateTime = rounded;
      const fireUpdate = (map) => {
        if (!map) return;
        map.options.dateTime = rounded;
        map.options.dateTimeInterval = this.activeLayerInterval;
        map.fire('datetimeupdated', { dateTime: rounded });
      };
      fireUpdate(this.leafletMap);
      fireUpdate(this.splitLeafletMap);
    },
    
    mapStepTime(minutes) {
      if (!this.mapDateTime) {
        this.mapDateTime = this.roundToInterval(new Date(), this.activeLayerInterval);
      }
      const step = minutes || this.activeLayerInterval;
      const newTime = new Date(this.mapDateTime.getTime() + step * 60 * 1000);
      this.setMapDateTime(newTime);
    },
    
    mapPlayPause() {
      this.mapIsPlaying = !this.mapIsPlaying;
      if (this.mapIsPlaying) {
        this.mapPlayInterval = setInterval(() => this.mapStepTime(), 800);
      } else {
        this.mapStopPlaying();
      }
    },
    
    mapStopPlaying() {
      this.mapIsPlaying = false;
      if (this.mapPlayInterval) { clearInterval(this.mapPlayInterval); this.mapPlayInterval = null; }
    },
    
    mapResetToNow() {
      this.mapStopPlaying();
      this.setMapDateTime(new Date());
    },
    
    destroyMap(mapProp, layerProp, markerProp) {
      if (this[markerProp]) { this[markerProp].remove(); this[markerProp] = null; }
      if (this[layerProp]) { this[layerProp] = null; }
      if (this[mapProp]) { this[mapProp].remove(); this[mapProp] = null; }
    },
    
    // Map element → layer mapping for syncing the element dropdown to the map layer
    elementToMapLayer(elementId) {
      const mapping = {
        'temp': 'temp', 'tempmax': 'temp', 'tempmin': 'temp',
        'feelslike': 'temp', 'feelslikemax': 'temp', 'feelslikemin': 'temp',
        'dew': 'temp',
        'precip': 'precip', 'precipprob': 'precip', 'snow': 'precip', 'snowdepth': 'precip',
        'windspeed': 'wind', 'windgust': 'wind', 'winddir': 'wind',
        'cloudcover': 'cloud',
        'pressure': 'pressure', 'sealevelpressure': 'pressure',
        'humidity': 'temp',
      };
      return mapping[elementId] || 'temp';
    },
    
    syncMapLayerToElement() {
      const layerId = this.elementToMapLayer(this.selectedElement);
      if (layerId !== this.activeMapLayer) {
        this.switchMapLayer(layerId);
      }
    },

    // Element picker methods
    openElementPicker() {
      this.elementPickerFocusGroup = null;
      this.showElementPicker = true;
    },

    onElementsSelected(selectedIds) {
      this.elementsString = selectedIds;
      this.showElementPicker = false;
      // If the currently selected display element is no longer in the list, reset to first available
      this.$nextTick(() => {
        const ids = this.availableElements.map(e => e.id);
        if (ids.length && !ids.includes(this.selectedElement)) {
          this.selectedElement = ids[0];
        }
      });
    },

    humanizeElementId(id) {
      return id
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .replace(/[_-]+/g, ' ')
        .replace(/\b\w/g, s => s.toUpperCase());
    },

    // Date helpers
    getDefaultStartDate() {
      const d = new Date();
      return d.toISOString().split('T')[0];
    },
    
    getDefaultEndDate() {
      const d = new Date();
      d.setDate(d.getDate() + 7);
      return d.toISOString().split('T')[0];
    },
    
    /** Returns the default forecast basis date (today) */
    getDefaultForecastBasisDate() {
      const d = new Date();
      return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
    },
    
    /** Returns the default forecast basis time (current hour) */
    getDefaultForecastBasisTime() {
      const d = new Date();
      return String(d.getHours()).padStart(2, '0') + ':00';
    },
    
    /** Reset forecast basis to live/now mode */
    setForecastBasisNow() {
      this.forecastBasisDate = this.getDefaultForecastBasisDate();
      this.forecastBasisTime = this.getDefaultForecastBasisTime();
      this.forecastBasisUserSetTime = false;
    },
    
    /** Convert forecastBasisDateTime to API param (yyyy-MM-ddTHH:mm format if not now, null if now) */
    getForecastBasisParam() {
      if (this.isForecastBasisNow) return null;
      return this.forecastBasisDateTime || null;
    },
    
    setDateRange(preset) {
      const today = new Date();
      this.startDate = today.toISOString().split('T')[0];
      
      const end = new Date(today);
      switch (preset) {
        case 'today':
          break;
        case '3days':
          end.setDate(end.getDate() + 3);
          break;
        case 'week':
          end.setDate(end.getDate() + 7);
          break;
        case '2weeks':
          end.setDate(end.getDate() + 14);
          break;
      }
      this.endDate = end.toISOString().split('T')[0];
    },
    
    // Location
    async geocodeLocation() {
      if (!this.locationText.trim()) return;
      
      this.isGeocoding = true;
      try {
        // Use VC geocoding API or browser geolocation
        const vcCommunicator = window.vcCommunicator;
        if (vcCommunicator && vcCommunicator.geocode) {
          const result = await vcCommunicator.geocode(this.locationText);
          if (result && result.length > 0) {
            this.location = {
              lat: result[0].latitude,
              lon: result[0].longitude,
              name: result[0].resolvedAddress || this.locationText
            };
          }
        } else {
          // Fallback: treat as coordinates if it looks like lat,lon
          const match = this.locationText.match(/^(-?\d+\.?\d*),\s*(-?\d+\.?\d*)$/);
          if (match) {
            this.location = {
              lat: parseFloat(match[1]),
              lon: parseFloat(match[2]),
              name: this.locationText
            };
          } else {
            this.location = {
              lat: 40.7128,
              lon: -74.006,
              name: this.locationText
            };
          }
        }
      } catch (err) {
        console.error('Geocoding error:', err);
      } finally {
        this.isGeocoding = false;
      }
    },
    
    useCurrentLocation() {
      if (!navigator.geolocation) return;
      
      this.isGeocoding = true;
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          this.location = {
            lat: pos.coords.latitude,
            lon: pos.coords.longitude,
            name: 'Current Location'
          };
          this.locationText = `${pos.coords.latitude.toFixed(4)}, ${pos.coords.longitude.toFixed(4)}`;
          this.isGeocoding = false;
        },
        (err) => {
          console.error('Geolocation error:', err);
          this.isGeocoding = false;
        }
      );
    },
    
    // Model selection
    toggleGroup(group) {
      this.expandedGroups[group] = !this.expandedGroups[group];
    },
    
    selectPreset(preset) {
      switch (preset) {
        case 'all':
          this.selectedModels = this.allModels.map(m => m.id);
          break;
        case 'none':
          this.selectedModels = [];
          break;
        case 'v2':
          // Select only V2 models (loaded from API)
          this.selectedModels = this.v2Models.map(m => m.id);
          this.expandedGroups.v2 = true;
          break;
        case 'legacy':
          // Select only legacy models
          this.selectedModels = LEGACY_MODELS.filter(m => m.isLegacy).map(m => m.id);
          this.expandedGroups.legacy = true;
          break;
      }
    },
    
    addV2DefaultModels() {
      // V2 default models matching GridDatasetManager._defaultV2ForecastReaders:
      // gfes, gfs, nam, hrrr, iconeu, usblended, ndfd
      const v2DefaultIds = ['gfes', 'gfs', 'nam', 'hrrr', 'iconeu', 'usblended', 'ndfd'];
      const available = this.v2Models.map(m => m.id);
      const toAdd = v2DefaultIds.filter(id => available.includes(id));
      const current = new Set(this.selectedModels);
      toAdd.forEach(id => current.add(id));
      this.selectedModels = [...current];
      this.expandedGroups.v2 = true;
    },
    
    addV2ExtendedModels() {
      // V2 extended models matching GridDatasetManager._extendedV2ForecastReaders:
      // default set + ecmwf, iconglobal, ukmo, ecmwfaifs, aigfs
      const v2ExtendedIds = ['gfes', 'gfs', 'nam', 'hrrr', 'iconeu', 'usblended', 'ndfd',
                             'ecmwf', 'iconglobal', 'ukmo', 'ecmwfaifs', 'aigfs'];
      const available = this.v2Models.map(m => m.id);
      const toAdd = v2ExtendedIds.filter(id => available.includes(id));
      const current = new Set(this.selectedModels);
      toAdd.forEach(id => current.add(id));
      this.selectedModels = [...current];
      this.expandedGroups.v2 = true;
    },
    
    getModelName(id) {
      const model = this.allModels.find(m => m.id === id);
      return model?.name || id;
    },

    /**
     * Quick Compare: iterate all models x all elements and compare to baseline.
     * Detects completely missing data, partially missing data, non-interpolated data,
     * and computes the core spread vs baseline for each combination.
     */
    runQuickCompare() {
      if (!this.canQuickCompare) return;
      this.quickCompareRunning = true;

      // Use nextTick so the spinner renders before heavy computation
      this.$nextTick(() => {
        try {
          const baselineId = this.baselineModel;
          const baselineData = this.queryData[baselineId];
          if (!baselineData || !baselineData.days) {
            this.quickCompareRunning = false;
            return;
          }

          // Models to compare (excluding baseline and observations)
          const models = this.selectedModelConfigs.filter(m => m && m.id !== baselineId && m.id !== '_observations');

          // Elements to check: use available (non-hidden) elements from dropdown
          const elements = this.availableElements.filter(el => !el.hidden);

          // Build baseline time index: element -> Map(epoch -> value)
          // Also collect all baseline epochs for coverage comparison
          const baselineEpochs = new Set();
          const collectEpochs = (days) => {
            if (!days) return;
            days.forEach(day => {
              if (this.timeResolution === 'minutes' && day.hours) {
                day.hours.forEach(h => {
                  if (h.minutes) h.minutes.forEach(m => baselineEpochs.add(m.datetimeEpoch));
                });
              } else if (this.timeResolution === 'hours' && day.hours) {
                day.hours.forEach(h => baselineEpochs.add(h.datetimeEpoch));
              } else {
                baselineEpochs.add(day.datetimeEpoch);
              }
            });
          };
          collectEpochs(baselineData.days);
          const baselineEpochCount = baselineEpochs.size;

          let totalMissing = 0;
          let totalPartial = 0;
          let totalNoInterp = 0;

          const rows = [];

          for (const el of elements) {
            const elementId = el.id;
            const elementName = el.name;

            // Get baseline values for this element
            const baselineValues = this.extractValues(baselineData, elementId);
            const baselineHasData = baselineValues.length > 0;

            // Build baseline epoch->value map
            const baseMap = new Map();
            if (baselineHasData) {
              this._forEachRecord(baselineData, (record) => {
                const v = getRecordValue(record, elementId);
                if (v !== undefined && v !== null) {
                  baseMap.set(record.datetimeEpoch, v);
                }
              });
            }

            let rowHasIssue = false;
            const cells = [];

            for (const model of models) {
              const modelData = this.queryData[model.id];
              let completelyMissing = false;
              let partiallyMissing = false;
              let notInterpolated = false;
              let rangePct = 100;
              let coverageText = '';
              let coverageClass = '';
              let spreadText = '';
              let spreadClass = '';
              let tooltip = '';
              let cssClass = '';

              if (!modelData || !modelData.days) {
                completelyMissing = true;
                totalMissing++;
                rowHasIssue = true;
                coverageText = '0%';
                coverageClass = 'qc-coverage-bad';
                tooltip = `${model.name}: No data at all for ${elementName}`;
                cssClass = 'qc-cell-missing';
              } else {
                const modelValues = this.extractValues(modelData, elementId);

                if (modelValues.length === 0) {
                  completelyMissing = true;
                  totalMissing++;
                  rowHasIssue = true;
                  coverageText = '0%';
                  coverageClass = 'qc-coverage-bad';
                  tooltip = `${model.name}: Element ${elementName} completely missing`;
                  cssClass = 'qc-cell-missing';
                } else {
                  // Build a set of epochs where this model has data for this element
                  const modelEpochSet = new Set();
                  let modelMinEpoch = Infinity;
                  let modelMaxEpoch = -Infinity;
                  this._forEachRecord(modelData, (record) => {
                    const v = getRecordValue(record, elementId);
                    if (v !== undefined && v !== null) {
                      modelEpochSet.add(record.datetimeEpoch);
                      if (record.datetimeEpoch < modelMinEpoch) modelMinEpoch = record.datetimeEpoch;
                      if (record.datetimeEpoch > modelMaxEpoch) modelMaxEpoch = record.datetimeEpoch;
                    }
                  });

                  // Determine the OVERLAPPING time range between baseline and model
                  const baseMinEpoch = Math.min(...baselineEpochs);
                  const baseMaxEpoch = Math.max(...baselineEpochs);
                  const overlapMin = Math.max(modelMinEpoch, baseMinEpoch);
                  const overlapMax = Math.min(modelMaxEpoch, baseMaxEpoch);

                  // Baseline epochs within the overlapping range only
                  const overlapBaselineEpochs = [];
                  for (const ep of baselineEpochs) {
                    if (ep >= overlapMin && ep <= overlapMax) {
                      overlapBaselineEpochs.push(ep);
                    }
                  }
                  const overlapCount = overlapBaselineEpochs.length;

                  // Count matches and diffs within the overlap range
                  let matchCount = 0;
                  let diffSum = 0;
                  let diffSqSum = 0;
                  let diffCount = 0;
                  let maxDiff = 0;

                  for (const ep of overlapBaselineEpochs) {
                    if (modelEpochSet.has(ep)) {
                      matchCount++;
                    }
                  }

                  // Compute diffs for all matching epochs (within overlap)
                  this._forEachRecord(modelData, (record) => {
                    const v = getRecordValue(record, elementId);
                    if (v !== undefined && v !== null) {
                      const baseVal = baseMap.get(record.datetimeEpoch);
                      if (baseVal !== undefined && baseVal !== null) {
                        const diff = v - baseVal;
                        diffSum += diff;
                        diffSqSum += diff * diff;
                        diffCount++;
                        if (Math.abs(diff) > Math.abs(maxDiff)) maxDiff = diff;
                      }
                    }
                  });

                  // Check for non-interpolated gaps WITHIN the model's own range
                  // Only meaningful when nointerpolate is on and the model's data
                  // has gaps at baseline time steps within its own min-max span
                  let hasNonInterp = false;
                  let interpGapCount = 0;
                  if (this.optionNointerpolate && overlapCount > 0) {
                    for (const ep of overlapBaselineEpochs) {
                      if (!modelEpochSet.has(ep)) interpGapCount++;
                    }
                    // Flag only if a significant portion of the overlap has gaps
                    // (small gaps are normal for coarser-resolution models)
                    if (interpGapCount > 0 && matchCount > 0) {
                      const gapRatio = interpGapCount / overlapCount;
                      // Only flag if >20% of overlap epochs are gaps (coarse native resolution)
                      if (gapRatio > 0.2) {
                        hasNonInterp = true;
                      }
                    }
                  }

                  notInterpolated = hasNonInterp;

                  // Coverage: based on overlap range, not total baseline
                  const coveragePct = overlapCount > 0 ? Math.round((matchCount / overlapCount) * 100) : 100;
                  // Also compute range coverage (how much of the baseline time span this model covers)
                  rangePct = baselineEpochCount > 0 ? Math.round((overlapCount / baselineEpochCount) * 100) : 100;

                  coverageText = coveragePct === 100 ? `${rangePct}%` : `${coveragePct}%`;

                  // Partially missing: gaps WITHIN the model's overlap range (not at the edges)
                  if (coveragePct < 100 && matchCount > 0 && overlapCount > 0) {
                    partiallyMissing = true;
                    totalPartial++;
                    rowHasIssue = true;
                    coverageClass = coveragePct < 50 ? 'qc-coverage-bad' : (coveragePct < 90 ? 'qc-coverage-warn' : 'qc-coverage-good');
                  } else if (rangePct < 100) {
                    // Model covers a shorter range but is complete within it — just informational
                    coverageClass = rangePct < 50 ? 'qc-coverage-warn' : 'qc-coverage-good';
                    coverageText = `${rangePct}%`;
                  } else {
                    coverageClass = 'qc-coverage-good';
                  }

                  if (notInterpolated) {
                    totalNoInterp++;
                    rowHasIssue = true;
                  }

                  // Spread (mean absolute difference from baseline)
                  if (diffCount > 0) {
                    const meanDiff = diffSum / diffCount;
                    const variance = diffSqSum / diffCount - meanDiff * meanDiff;
                    const stdDev = Math.sqrt(Math.max(0, variance));
                    const mad = Math.abs(meanDiff);

                    // Use MAD for spread display
                    spreadText = '\u00b1' + mad.toFixed(1);
                    if (mad < 1) {
                      spreadClass = 'qc-spread-low';
                    } else if (mad < 5) {
                      spreadClass = 'qc-spread-med';
                    } else {
                      spreadClass = 'qc-spread-high';
                      rowHasIssue = true;
                    }

                    const rangeNote = rangePct < 100 ? `\nRange: ${rangePct}% of baseline time span` : '';
                    tooltip = `${model.name} vs ${this.getModelName(baselineId)} for ${elementName}:\n` +
                      `Coverage: ${coveragePct}% within overlap (${matchCount}/${overlapCount})${rangeNote}\n` +
                      `Mean diff: ${meanDiff >= 0 ? '+' : ''}${meanDiff.toFixed(2)}\n` +
                      `Max diff: ${maxDiff >= 0 ? '+' : ''}${maxDiff.toFixed(2)}\n` +
                      `Std dev: ${stdDev.toFixed(2)}`;
                    if (hasNonInterp) tooltip += `\nNon-interpolated gaps: ${interpGapCount} of ${overlapCount}`;
                  } else {
                    tooltip = `${model.name}: ${modelValues.length} values, no overlap with baseline`;
                  }

                  if (completelyMissing) {
                    cssClass = 'qc-cell-missing';
                  } else if (partiallyMissing && notInterpolated) {
                    cssClass = 'qc-cell-partial-nointerp';
                  } else if (partiallyMissing) {
                    cssClass = 'qc-cell-partial';
                  } else if (notInterpolated) {
                    cssClass = 'qc-cell-nointerp';
                  } else {
                    cssClass = 'qc-cell-ok';
                  }
                }
              }

              cells.push({
                modelId: model.id,
                completelyMissing,
                partiallyMissing,
                notInterpolated,
                shorterRange: !completelyMissing && rangePct < 100,
                rangePct: completelyMissing ? 0 : rangePct,
                coverageText,
                coverageClass,
                spreadText,
                spreadClass,
                tooltip,
                cssClass
              });
            }

            rows.push({
              elementId,
              elementName,
              hasIssue: rowHasIssue,
              cells
            });
          }

          // Sort: rows with issues first
          rows.sort((a, b) => {
            if (a.hasIssue && !b.hasIssue) return -1;
            if (!a.hasIssue && b.hasIssue) return 1;
            return 0;
          });

          this.quickCompareResults = {
            models,
            rows,
            modelCount: models.length,
            elementCount: elements.length,
            totalMissing,
            totalPartial,
            totalNoInterp
          };

          this.showQuickCompare = true;
        } finally {
          this.quickCompareRunning = false;
        }
      });
    },

    /**
     * Iterate over all records (day/hour/minute) in model data,
     * calling callback(record) for each one based on current timeResolution.
     */
    _forEachRecord(modelData, callback) {
      if (!modelData || !modelData.days) return;
      modelData.days.forEach(day => {
        if (this.timeResolution === 'minutes' && day.hours) {
          day.hours.forEach(h => {
            if (h.minutes) h.minutes.forEach(m => callback(m));
          });
        } else if (this.timeResolution === 'hours' && day.hours) {
          day.hours.forEach(h => callback(h));
        } else {
          callback(day);
        }
      });
    },

    /**
     * Open the chart for a specific element from the Quick Compare table.
     */
    openQuickCompareChart(elementId) {
      this.selectedElement = elementId;
      this.viewMode = 'chartgrid';
      this.showQuickCompare = false;
      this.$nextTick(() => this.renderChart());
    },

    copyQueryUrl(url) {
      navigator.clipboard.writeText(url).then(() => {
        // Brief visual feedback could be added here
      }).catch(() => {
        // Fallback: select from a temporary input
        const input = document.createElement('input');
        input.value = url;
        document.body.appendChild(input);
        input.select();
        document.execCommand('copy');
        document.body.removeChild(input);
      });
    },
    
    // Get model display name with legacy indicator
    getModelDisplayName(id) {
      const model = this.allModels.find(m => m.id === id);
      const name = model?.name || id;
      if (model && model.isLegacy) {
        return name + ' ⚠️';  // Legacy indicator
      }
      return name;
    },
    
    // Check if a model is a V2 model (loaded from API, not legacy)
    isV2Model(modelId) {
      const model = this.allModels.find(m => m.id === modelId);
      return model && !model.isLegacy && model.id !== 'default';
    },
    
    // Check if this is a legacy model
    isLegacyModel(modelId) {
      const model = this.allModels.find(m => m.id === modelId);
      return model && model.isLegacy;
    },
    
    // Check if a model's bounds contain the current location
    isModelInBounds(model) {
      if (!model || !model.bounds || !this.location) return true;  // No bounds = global
      const { lat, lon } = this.location;
      const { minLat, maxLat, minLon, maxLon } = model.bounds;
      return lat >= minLat && lat <= maxLat && lon >= minLon && lon <= maxLon;
    },
    
    // Legacy compatibility - no longer needed but kept for template references
    hasV2Version(modelId) {
      return false;  // Simplified - legacy/V2 now determined by isLegacy flag
    },
    
    // Get the base model name (without _2 suffix)
    getBaseModelId(modelId) {
      if (modelId.endsWith('_2')) {
        return modelId.slice(0, -2);
      }
      return modelId;
    },
    
    // Load V2 models from API
    async loadV2Models() {
      if (this.v2ModelsLoaded || this.v2ModelsLoading) return;
      
      const apiKey = this.weatherApiKey;
      if (!apiKey) {
        console.log('No API key, skipping V2 models load');
        return;
      }
      
      this.v2ModelsLoading = true;
      
      try {
        const result = await loadModelsFromApi(this.serverRoot, apiKey);
        
        if (result.fromApi && result.modelsConfig) {
          // Convert API models to our format
          // Use the 'model' field from API for forecastDataset (e.g., 'ukmo_global_2')
          // The 'id' field is for display/selection (e.g., 'ukmo')
          // Filter out models that are already hardcoded in LEGACY_MODELS (e.g., blended models)
          const hardcodedIds = new Set(LEGACY_MODELS.map(m => m.id));
          this.v2Models = result.modelsConfig
            .filter(modelConfig => !hardcodedIds.has(modelConfig.id))
            .map((modelConfig, index) => ({
            id: modelConfig.id,  // Internal ID for component (e.g., 'ukmo')
            apiModelId: modelConfig.model || modelConfig.id,  // API value for forecastDataset (e.g., 'ukmo_global_2')
            name: result.modelNames[modelConfig.id] || modelConfig.id,
            color: V2_COLORS[index % V2_COLORS.length],
            group: 'v2',
            isLegacy: false,
            isV2: true,
            useV2Forecast: true,  // V2 models must send usev2forecast option to use V2 data paths
            bounds: modelConfig.bounds  // Include bounds for potential filtering
          }));
          
          console.log(`Loaded ${this.v2Models.length} V2 models from API`);
        }
        
        this.v2ModelsLoaded = true;
      } catch (err) {
        console.error('Failed to load V2 models:', err);
      } finally {
        this.v2ModelsLoading = false;
      }
    },
    
    // Model Runs methods
    async onModelRunsToggle(enable) {
      // Do nothing if already in the requested mode
      if (enable === this.showModelRuns) return;
      
      this.showModelRuns = enable;
      
      // Clear chart data when switching modes
      this.queryData = null;
      this.attributionData = {};
      
      if (this.showModelRuns) {
        // Switching to Model Runs mode - clear selected models
        this.selectedModels = [];
        await this.fetchModelRuns();
      } else {
        // Switching to Models mode - clear runs and set default model
        this.selectedModelRuns = [];
        this.modelRunsData = {};
        this.expandedModelRuns = {};
        this.selectedModels = ['default_v2'];
      }
    },
    
    async fetchModelRuns() {
      const apiKey = this.weatherApiKey;
      if (!apiKey) {
        console.log('No API key, cannot fetch model runs');
        return;
      }
      
      this.modelRunsLoading = true;
      
      try {
        // Calculate date range for last 5 days
        const endDate = new Date();
        const startDate = new Date();
        startDate.setDate(startDate.getDate() - 5);
        
        const datestart = startDate.toISOString().split('T')[0];
        const dateend = endDate.toISOString().split('T')[0];
        
        // Ensure SERVER_ROOT ends with slash
        const serverRoot = this.serverRoot.endsWith('/') ? this.serverRoot : this.serverRoot + '/';
        const url = `${serverRoot}VisualCrossingWebServices/rest/services/models?key=${apiKey}&datestart=${datestart}&dateend=${dateend}`;
        
        const response = await fetch(url);
        if (!response.ok) throw new Error(`API error: ${response.status}`);
        
        const data = await response.json();
        
        // Process and store model runs data
        this.modelRunsData = {};
        if (data.models && Array.isArray(data.models)) {
          data.models.forEach(model => {
            if (model.availableRuns && model.availableRuns.length > 0) {
              this.modelRunsData[model.id] = {
                model: model.model,
                runs: model.availableRuns,
                bounds: model.bounds
              };
            }
          });
        }
        
        console.log(`Loaded runs for ${Object.keys(this.modelRunsData).length} models`);
      } catch (err) {
        console.error('Failed to fetch model runs:', err);
      } finally {
        this.modelRunsLoading = false;
      }
    },
    
    hasAvailableRuns(modelId) {
      // Check by model ID directly or by apiModelId mapping
      const model = this.allModels.find(m => m.id === modelId);
      const checkId = model?.apiModelId || modelId;
      return !!(this.modelRunsData[modelId] || this.modelRunsData[checkId]);
    },
    
    getAvailableRuns(modelId) {
      const model = this.allModels.find(m => m.id === modelId);
      const checkId = model?.apiModelId || modelId;
      const data = this.modelRunsData[modelId] || this.modelRunsData[checkId];
      return data?.runs || [];
    },
    
    getAvailableRunsCount(modelId) {
      return this.getAvailableRuns(modelId).length;
    },
    
    toggleModelRuns(modelId) {
      this.expandedModelRuns[modelId] = !this.expandedModelRuns[modelId];
    },
    
    isRunSelected(modelId, runId) {
      return this.selectedModelRuns.some(r => r.modelId === modelId && r.runId === runId);
    },
    
    toggleRunSelection(modelId, run) {
      const idx = this.selectedModelRuns.findIndex(r => r.modelId === modelId && r.runId === run.runId);
      if (idx >= 0) {
        this.selectedModelRuns.splice(idx, 1);
      } else {
        const model = this.allModels.find(m => m.id === modelId);
        // Get the API model ID from the runs data
        const runsData = this.modelRunsData[modelId] || this.modelRunsData[model?.apiModelId || modelId];
        this.selectedModelRuns.push({
          modelId: modelId,
          runId: run.runId,
          runTime: run.runTime,
          displayName: model?.name || modelId,
          apiModelId: runsData?.model || model?.apiModelId || modelId,
          color: model?.color || '#888'
        });
      }
    },
    
    removeSelectedRun(idx) {
      this.selectedModelRuns.splice(idx, 1);
    },
    
    clearSelectedRuns() {
      this.selectedModelRuns = [];
    },
    
    formatRunTime(runTime) {
      if (!runTime) return '';
      const d = new Date(runTime);
      // Display in UTC without timezone conversion
      return d.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
        timeZone: 'UTC'
      }) + ' UTC';
    },
    
    formatRunHour(runTime) {
      if (!runTime) return '';
      const d = new Date(runTime);
      // Just return the hour in UTC
      return d.toLocaleString('en-US', {
        hour: 'numeric',
        hour12: false,
        timeZone: 'UTC'
      });
    },
    
    getRunsByDate(modelId) {
      const runs = this.getAvailableRuns(modelId);
      const dateMap = {};
      
      runs.forEach(run => {
        const d = new Date(run.runTime);
        const dateKey = d.toISOString().split('T')[0]; // YYYY-MM-DD
        const dateLabel = d.toLocaleDateString('en-US', {
          weekday: 'short',
          month: 'short',
          day: 'numeric',
          timeZone: 'UTC'
        });
        
        if (!dateMap[dateKey]) {
          dateMap[dateKey] = {
            date: dateKey,
            dateLabel: dateLabel,
            runs: []
          };
        }
        dateMap[dateKey].runs.push(run);
      });
      
      // Sort by date descending (most recent first) and runs by time
      return Object.values(dateMap)
        .sort((a, b) => b.date.localeCompare(a.date))
        .map(group => ({
          ...group,
          runs: group.runs.sort((a, b) => new Date(b.runTime) - new Date(a.runTime))
        }));
    },
    
    getRunColor(idx) {
      // Use a color palette for runs
      const runColors = [
        '#1E88E5', '#43A047', '#FB8C00', '#8E24AA', '#00ACC1',
        '#D81B60', '#3949AB', '#00897B', '#FFB300', '#6D4C41',
        '#546E7A', '#7CB342', '#C0CA33', '#F4511E', '#5E35B1'
      ];
      return runColors[idx % runColors.length];
    },
    
    // Split view resize methods
    startSplitResize(e) {
      this.isResizingSplit = true;
      this.splitResizeStartY = e.type === 'touchstart' ? e.touches[0].clientY : e.clientY;
      this.splitResizeStartHeight = this.splitChartHeight;
      
      document.addEventListener('mousemove', this.onSplitResize);
      document.addEventListener('mouseup', this.stopSplitResize);
      document.addEventListener('touchmove', this.onSplitResize);
      document.addEventListener('touchend', this.stopSplitResize);
      
      // Prevent text selection during resize
      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'row-resize';
    },
    
    onSplitResize(e) {
      if (!this.isResizingSplit) return;
      
      const clientY = e.type === 'touchmove' ? e.touches[0].clientY : e.clientY;
      const delta = clientY - this.splitResizeStartY;
      const newHeight = this.splitResizeStartHeight + delta;
      
      // Set min/max bounds for chart height
      const container = this.$refs.splitViewContainer;
      const containerHeight = container ? container.clientHeight : 600;
      const minHeight = 150;
      const maxHeight = containerHeight - 200; // Leave room for map
      
      this.splitChartHeight = Math.max(minHeight, Math.min(maxHeight, newHeight));
      
      // Trigger chart re-render on resize
      this.$nextTick(() => {
        this.renderChart();
      });
    },
    
    stopSplitResize() {
      this.isResizingSplit = false;
      
      document.removeEventListener('mousemove', this.onSplitResize);
      document.removeEventListener('mouseup', this.stopSplitResize);
      document.removeEventListener('touchmove', this.onSplitResize);
      document.removeEventListener('touchend', this.stopSplitResize);
      
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
      
      // Final chart re-render
      this.$nextTick(() => {
        this.renderChart();
      });
    },

    // Chart+Grid split resize methods
    startChartgridResize(e) {
      this.isResizingChartgrid = true;
      this.chartgridResizeStartY = e.type === 'touchstart' ? e.touches[0].clientY : e.clientY;
      this.chartgridResizeStartHeight = this.chartgridChartHeight;
      
      document.addEventListener('mousemove', this.onChartgridResize);
      document.addEventListener('mouseup', this.stopChartgridResize);
      document.addEventListener('touchmove', this.onChartgridResize);
      document.addEventListener('touchend', this.stopChartgridResize);
      
      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'row-resize';
    },
    
    onChartgridResize(e) {
      if (!this.isResizingChartgrid) return;
      
      const clientY = e.type === 'touchmove' ? e.touches[0].clientY : e.clientY;
      const delta = clientY - this.chartgridResizeStartY;
      const newHeight = this.chartgridResizeStartHeight + delta;
      
      const container = this.$refs.chartgridViewContainer;
      const containerHeight = container ? container.clientHeight : 600;
      const minHeight = 120;
      const maxHeight = containerHeight - 200;
      
      this.chartgridChartHeight = Math.max(minHeight, Math.min(maxHeight, newHeight));
      
      this.$nextTick(() => {
        this.renderChart();
      });
    },
    
    stopChartgridResize() {
      this.isResizingChartgrid = false;
      
      document.removeEventListener('mousemove', this.onChartgridResize);
      document.removeEventListener('mouseup', this.stopChartgridResize);
      document.removeEventListener('touchmove', this.onChartgridResize);
      document.removeEventListener('touchend', this.stopChartgridResize);
      
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
      
      this.$nextTick(() => {
        this.renderChart();
      });
    },
    
    // Data fetching
    async fetchData() {
      if (!this.canFetch) return;
      
      this.isLoading = true;
      this.loadingProgress = 0;
      this.queryData = {};
      this.queryResponseTimes = {};
      this.attributionData = {};
      
      const vcCommunicator = window.vcCommunicator;
      const models = [...this.selectedModels];
      
      // Build list of all items to fetch: regular models + selected runs
      const fetchItems = [];
      
      // Track which models have runs selected so we can optionally skip fetching the "latest" run
      const modelsWithSelectedRuns = new Set();
      if (this.showModelRuns && this.selectedModelRuns.length > 0) {
        this.selectedModelRuns.forEach(run => {
          modelsWithSelectedRuns.add(run.modelId);
        });
      }
      
      // Add regular models (only if they're selected AND don't have specific runs selected)
      // If user selected specific runs for a model, skip the "default/latest" fetch for that model
      models.forEach(modelId => {
        // Skip if this model has specific runs selected - we'll only fetch the selected runs
        if (modelsWithSelectedRuns.has(modelId)) {
          return;
        }
        fetchItems.push({
          modelId: modelId,
          runId: null,
          displayKey: modelId  // Key used in queryData
        });
      });
      
      // Add selected model runs (if model runs mode is enabled)
      // These are fetched regardless of whether the parent model checkbox is checked
      if (this.showModelRuns && this.selectedModelRuns.length > 0) {
        this.selectedModelRuns.forEach((run, idx) => {
          fetchItems.push({
            modelId: run.modelId,
            runId: run.runId,
            apiModelId: run.apiModelId,
            displayKey: `${run.modelId}_run_${run.runId}`,  // Unique key for this run
            displayName: `${run.displayName} (${this.formatRunTime(run.runTime)})`,
            color: this.getRunColor(idx)
          });
        });
      }
      
      const totalItems = fetchItems.length;
      
      try {
        for (let i = 0; i < fetchItems.length; i++) {
          const item = fetchItems[i];
          const modelId = item.modelId;
          this.loadingProgress = ((i + 1) / totalItems) * 100;
          
          // Use locationText for API request - the API can resolve location names
          // This allows "paris,france" to work directly without client-side geocoding
          const locationStr = this.locationText.trim() || `${this.location.lat},${this.location.lon}`;
          const include = this.timeResolution === 'minutes' ? 'minutes' : (this.timeResolution === 'hours' ? 'hours' : 'days');
          
          // Compute forecastBasisDate param first — needed to decide options
          const forecastBasisParam = this.getForecastBasisParam();
          
          // Build options string from checkboxes
          const optionsList = [];
          // Add minuteinterval option when using minutely resolution
          if (this.timeResolution === 'minutes' && this.minuteInterval) {
            optionsList.push(`minuteinterval_${this.minuteInterval}`);
          }
          // Don't include usefcst when using a historical forecastBasisDate — it conflicts
          // with the historical forecast path. Only include it for live/"now" requests.
          if (!forecastBasisParam && this.optionUsefcst) optionsList.push('usefcst');
          // Only include forecastbasisdatelocal for v2 models — v1 models use UTC date-only
          const modelConfigForOptions = this.allModels.find(m => m.id === modelId);
          const isV2Model = !!modelConfigForOptions?.useV2Forecast;
          if (forecastBasisParam && isV2Model && this.forecastBasisTimezone === 'local') optionsList.push('forecastbasisdatelocal');
          if (this.optionNointerpolate) optionsList.push('nointerpolate');
          if (this.optionAttribution) optionsList.push('attribution');
          // Add usev2forecast for models with useV2Forecast flag
          if (isV2Model) optionsList.push('usev2forecast');
          // Add trackmodelchanges for default/default_v2/default_v2_extended when option is enabled
          if (this.optionTrackModelChanges && (modelId === 'default' || modelId === 'default_v2' || modelId === 'default_v2_extended')) {
            optionsList.push('trackmodelchanges');
          }
          // Add useextendedblend for models with useExtendedBlend flag
          if (modelConfigForOptions?.useExtendedBlend) {
            optionsList.push('useextendedblend');
          }
          const optionsStr = optionsList.join(',');
          
          // Build query definition object for vcCommunicator
          const elementsStr = this.isDefaultElements ? '' : this.selectedElementIds.join(',');
          const queryDefn = {
            startdate: this.startDate,
            enddate: this.endDate,
            include: include,
            options: optionsStr,
            unitGroup: this.unitGroup
          };
          if (elementsStr) queryDefn.elements = elementsStr;
          
          // Add forecastBasisDate when not using current/latest models
          if (forecastBasisParam) {
            // V1 models expect date-only (yyyy-MM-dd); V2 models support full datetime
            queryDefn.forecastBasisDate = isV2Model ? forecastBasisParam : forecastBasisParam.substring(0, 10);
          }
          
          // Add model/forecastDataset unless model has noForecastDataset flag
          const modelConfig = this.allModels.find(m => m.id === modelId);
          if (modelId && !(modelConfig && modelConfig.noForecastDataset)) {
            // Use apiModelId if available (for legacy models), otherwise use the id directly
            // For runs, use the apiModelId from the run data
            let forecastDataset = item.apiModelId || modelConfig?.apiModelId || modelId;
            
            // Append runId to forecastDataset with colon separator (e.g., iconglobal_2:20260127_12)
            if (item.runId) {
              forecastDataset = `${forecastDataset}:${item.runId}`;
            }
            
            queryDefn.forecastDataset = forecastDataset;
          }
          
          try {
            let data;
            const fetchStartTime = performance.now();
            if (vcCommunicator && vcCommunicator.retrieveTimelineData) {
              // vcCommunicator.retrieveTimelineData(defn, location, name, contentType, requireKey, forceGlobalKey)
              data = await vcCommunicator.retrieveTimelineData(queryDefn, locationStr, null, "json", true, false, this.serverRoot);
            } else {
              // Fallback to direct API call
              // Use apiModelId for legacy models, otherwise use the id
              let apiModel = item.apiModelId || modelConfig?.apiModelId || modelId;
              
              // Append runId to model with colon separator (e.g., iconglobal_2:20260127_12)
              if (item.runId) {
                apiModel = `${apiModel}:${item.runId}`;
              }
              
              const fallbackOptions = [optionsStr];
              if (apiModel) fallbackOptions.push(`models:${apiModel}`);
              const options = {
                location: locationStr,
                startdate: this.startDate,
                enddate: this.endDate,
                include: include,
                options: fallbackOptions.join(','),
                forecastBasisDate: forecastBasisParam || undefined
              };
              if (elementsStr) options.elements = elementsStr;
              data = await this.fetchFromApi(options);
            }
            const fetchEndTime = performance.now();
            this.queryResponseTimes[item.displayKey] = Math.round(fetchEndTime - fetchStartTime);
            
            if (data) {
              // Clean up data for usefcst: remove non-forecast records from both daily and hourly levels
              if (forecastBasisParam && this.optionUsefcst && data.days) {
                data.days.forEach(day => {
                  // Filter hourly records to keep only forecast sources
                  if (day.hours) {
                    day.hours = day.hours.filter(h => h.source === 'fcst');
                  }
                });
                // Filter daily records to keep only forecast sources
                data.days = data.days.filter(day => day.source === 'fcst');
              }
              // Use displayKey to store data (allows multiple runs of same model)
              const dataKey = item.displayKey;
              this.queryData[dataKey] = data;
              // Store attribution if present
              if (data.attribution) {
                this.attributionData[dataKey] = data.attribution;
              }
              
              // Sync location from first API response (API returns resolved coordinates)
              if (data.latitude !== undefined && data.longitude !== undefined) {
                const apiLat = parseFloat(data.latitude);
                const apiLon = parseFloat(data.longitude);
                // Update location if it differs from what we have
                if (this.location.lat !== apiLat || this.location.lon !== apiLon) {
                  this.location = {
                    lat: apiLat,
                    lon: apiLon,
                    name: data.resolvedAddress || this.location.name
                  };
                  // Update locationText to show resolved coordinates/address
                  if (data.resolvedAddress) {
                    this.locationText = data.resolvedAddress;
                  }
                }
              }
              
              // For runs, add a dynamic entry to track for display purposes
              if (item.runId && item.displayName) {
                // Create a temporary model config for the chart/table display
                data._runDisplayConfig = {
                  id: dataKey,
                  name: item.displayName,
                  color: item.color,
                  isRun: true
                };
              }
            }
          } catch (modelErr) {
            console.warn(`Failed to fetch ${item.displayKey}:`, modelErr);
          }
        }
        
        // After fetching all model data, optionally fetch actual observations for comparison
        if (this.optionCompareWithObs && !this.isForecastBasisNow) {
          try {
            const locationStr = this.locationText.trim() || `${this.location.lat},${this.location.lon}`;
            const include = this.timeResolution === 'minutes' ? 'minutes' : (this.timeResolution === 'hours' ? 'hours' : 'days');
            const obsOptionsList = ['useobs'];
            if (this.timeResolution === 'minutes' && this.minuteInterval) {
              obsOptionsList.push(`minuteinterval_${this.minuteInterval}`);
            }
            const elementsStr = this.isDefaultElements ? '' : this.selectedElementIds.join(',');
            const obsQueryDefn = {
              startdate: this.startDate,
              enddate: this.endDate,
              include: include,
              options: obsOptionsList.join(','),
              unitGroup: this.unitGroup
            };
            if (elementsStr) obsQueryDefn.elements = elementsStr;
            
            let obsData;
            const vcCommunicator = window.vcCommunicator;
            if (vcCommunicator && vcCommunicator.retrieveTimelineData) {
              obsData = await vcCommunicator.retrieveTimelineData(obsQueryDefn, locationStr, null, "json", true, false, this.serverRoot);
            } else {
              const obsFallbackOptions = {
                location: locationStr,
                startdate: this.startDate,
                enddate: this.endDate,
                include: include,
                options: obsOptionsList.join(',')
              };
              if (elementsStr) obsFallbackOptions.elements = elementsStr;
              obsData = await this.fetchFromApi(obsFallbackOptions);
            }
            
            if (obsData) {
              this.queryData['_observations'] = obsData;
            }
          } catch (obsErr) {
            console.warn('Failed to fetch observation data:', obsErr);
          }
        }
        
        // Save fetch parameters to detect future changes
        this.lastFetchParams = JSON.parse(JSON.stringify(this.currentFetchParams));
        
        this.$nextTick(() => {
          this.renderChart();
        });
      } catch (err) {
        console.error('Data fetch error:', err);
      } finally {
        this.isLoading = false;
      }
    },
    
    async fetchFromApi(options) {
      const apiKey = this.weatherApiKey;
      if (!apiKey) {
        console.error('No API key available');
        return null;
      }
      
      const baseUrl = this.serverRoot + '/VisualCrossingWebServices/rest/services/timeline';
      const unitGroup = options.unitGroup || this.unitGroup || 'metric';
      let url = `${baseUrl}/${encodeURIComponent(options.location)}/${options.startdate}/${options.enddate}?key=${apiKey}&include=${options.include}&options=${options.options}&unitGroup=${unitGroup}`;
      if (options.elements) url += `&elements=${encodeURIComponent(options.elements)}`;
      
      // Add forecastBasisDate if present
      if (options.forecastBasisDate) {
        url += `&forecastBasisDate=${encodeURIComponent(options.forecastBasisDate)}`;
      }
      
      const response = await fetch(url);
      if (!response.ok) throw new Error(`API error: ${response.status}`);
      return response.json();
    },
    
    /**
     * Parse the stations field from trackmodelchanges response.
     * 
     * New format (pipe-separated, with blend weights):
     *   "temp:hrrr_2:25%,gfs_2:17%,nam_2:14% | precip:usblended_2 | snow:hrrr_2"
     * 
     * Legacy format (comma-separated, single source per element):
     *   "temp:vcusblended, precip:vcusblended, snow:gfs"
     * 
     * We auto-detect the format by checking for pipe separators.
     * Output: { temp: 'hrrr_2:25%,gfs_2:17%,nam_2:14%', precip: 'usblended_2', snow: 'hrrr_2' }
     */
    parseStationsSources(stationsField) {
      if (!stationsField) return null;
      
      // Handle both string and array formats
      let entries;
      if (Array.isArray(stationsField)) {
        entries = stationsField;
      } else if (typeof stationsField === 'string') {
        entries = [stationsField];
      } else {
        return null;
      }
      
      if (entries.length === 0) return null;
      
      // Parse each entry into element:source pairs
      const allSources = {};  // element -> Set of sources
      entries.forEach(entry => {
        if (typeof entry !== 'string') return;
        
        // Auto-detect format: pipes mean new format, otherwise legacy comma-separated
        const usePipeSeparator = entry.includes(' | ');
        const parts = usePipeSeparator ? entry.split(' | ') : entry.split(',');
        
        parts.forEach(part => {
          const trimmed = part.trim();
          if (!trimmed) return;
          const colonIdx = trimmed.indexOf(':');
          if (colonIdx > 0) {
            const element = trimmed.substring(0, colonIdx).trim();
            const source = trimmed.substring(colonIdx + 1).trim();
            if (element && source) {
              if (!allSources[element]) allSources[element] = new Set();
              allSources[element].add(source);
            }
          }
        });
      });
      
      if (Object.keys(allSources).length === 0) return null;
      
      // Build result: if multiple sources for an element, join them
      const result = {};
      for (const [element, sourceSet] of Object.entries(allSources)) {
        const sources = Array.from(sourceSet);
        result[element] = sources.length === 1 ? sources[0] : sources.join('/');
      }
      return result;
    },

    /**
     * Get the model source for a specific element at a given time.
     * Uses the parsed stations field from trackmodelchanges.
     */
    getSourceAtTime(modelData, datetimeEpoch, element) {
      if (!modelData || !modelData.days) return null;
      for (const day of modelData.days) {
        if (this.timeResolution === 'minutes' && day.hours) {
          for (const hour of day.hours) {
            if (hour.minutes) {
              const min = hour.minutes.find(m => m.datetimeEpoch === datetimeEpoch);
              if (min) {
                const sources = this.parseStationsSources(min.stations);
                return sources ? sources[element] || null : null;
              }
            }
          }
        } else if (this.timeResolution === 'hours' && day.hours) {
          const hour = day.hours.find(h => h.datetimeEpoch === datetimeEpoch);
          if (hour) {
            const sources = this.parseStationsSources(hour.stations);
            return sources ? sources[element] || null : null;
          }
        } else if (day.datetimeEpoch === datetimeEpoch) {
          const sources = this.parseStationsSources(day.stations);
          return sources ? sources[element] || null : null;
        }
      }
      return null;
    },

    // Data extraction
    getValueAtTime(modelData, datetimeEpoch, element) {
      if (!modelData.days) return null;
      
      for (const day of modelData.days) {
        if (this.timeResolution === 'minutes' && day.hours) {
          for (const hour of day.hours) {
            if (hour.minutes) {
              const min = hour.minutes.find(m => m.datetimeEpoch === datetimeEpoch);
              if (min) {
                const v = getRecordValue(min, element);
                if (v !== undefined) return v;
              }
            }
          }
        } else if (this.timeResolution === 'hours' && day.hours) {
          const hour = day.hours.find(h => h.datetimeEpoch === datetimeEpoch);
          if (hour) {
            const v = getRecordValue(hour, element);
            if (v !== undefined) return v;
          }
        } else if (day.datetimeEpoch === datetimeEpoch) {
          const v = getRecordValue(day, element);
          if (v !== undefined) return v;
        }
      }
      return null;
    },
    
    extractValues(modelData, element) {
      const values = [];
      if (!modelData.days) return values;
      
      modelData.days.forEach(day => {
        if (this.timeResolution === 'minutes' && day.hours) {
          day.hours.forEach(hour => {
            if (hour.minutes) {
              hour.minutes.forEach(min => {
                const v = getRecordValue(min, element);
                if (v !== undefined && v !== null) {
                  values.push(v);
                }
              });
            }
          });
        } else if (this.timeResolution === 'hours' && day.hours) {
          day.hours.forEach(hour => {
            const v = getRecordValue(hour, element);
            if (v !== undefined && v !== null) {
              values.push(v);
            }
          });
        } else {
          const v = getRecordValue(day, element);
          if (v !== undefined && v !== null) {
            values.push(v);
          }
        }
      });
      
      return values;
    },
    
    /** Check if a model has spread data in the current query results */
    modelHasSpread(modelId) {
      const modelData = this.queryData?.[modelId];
      if (!modelData?.days) return false;
      for (const day of modelData.days) {
        if (day.hours) {
          for (const hour of day.hours) {
            if (hour.spread) return true;
            if (hour.minutes) {
              for (const min of hour.minutes) {
                if (min.spread) return true;
              }
            }
          }
        }
        if (day.spread) return true;
      }
      return false;
    },
    
    // Formatting
    formatValue(val) {
      if (val === null || val === undefined) return '-';
      return typeof val === 'number' ? val.toFixed(1) : val;
    },
    
    formatDateTime(epoch) {
      const d = new Date(epoch * 1000);
      if (this.timeResolution === 'minutes') {
        return d.toLocaleString(undefined, { 
          month: 'short', 
          day: 'numeric', 
          hour: 'numeric',
          minute: '2-digit'
        });
      }
      if (this.timeResolution === 'hours') {
        return d.toLocaleString(undefined, { 
          month: 'short', 
          day: 'numeric', 
          hour: 'numeric'
        });
      }
      return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    },
    
    getCellClass(row, modelId) {
      if (!this.baselineModel || modelId === this.baselineModel) return '';
      
      const baseVal = row[this.baselineModel];
      const val = row[modelId];
      
      if (baseVal === null || val === null) return '';
      
      const diff = val - baseVal;
      if (Math.abs(diff) < 1) return 'cell-similar';
      return diff > 0 ? 'cell-higher' : 'cell-lower';
    },
    
    highlightModel(modelId) {
      this.highlightedModel = modelId;
      this.updateChartHighlight(modelId);
    },
    
    updateChartHighlight(modelId) {
      // Update all chart SVGs to highlight/dim model lines and points
      const svgs = [
        this.$refs.chartSvg,
        this.$refs.splitChartSvg,
        this.$refs.chartgridChartSvg
      ].filter(Boolean);
      
      svgs.forEach(svgEl => {
        const svg = d3.select(svgEl);
        if (modelId) {
          // Dim all lines, points, and spreads
          svg.selectAll('.model-line, .model-line-gap').attr('stroke-opacity', 0.08);
          svg.selectAll('.data-point').attr('opacity', 0.1);
          svg.selectAll('.ensemble-spread').attr('fill-opacity', 0.05);
          // Highlight the selected model
          svg.selectAll(`.model-${CSS.escape(modelId)}`).filter('.model-line').attr('stroke-opacity', 0.9).attr('stroke-width', function() {
            return parseFloat(d3.select(this).attr('stroke-width')) * 1.5;
          });
          svg.selectAll(`.model-${CSS.escape(modelId)}`).filter('.model-line-gap').attr('stroke-opacity', 0.5);
          svg.selectAll(`.data-point-${CSS.escape(modelId)}`).attr('opacity', 1);
          svg.selectAll(`.ensemble-${CSS.escape(modelId)}`).attr('fill-opacity', 0.4);
        } else {
          // Re-render to restore original opacities cleanly
          this.renderChart();
        }
      });
    },
    
    // Chart rendering
    renderChart() {
      if (!this.hasData) return;
      
      // Render to main chart if in chart/diff mode
      if (this.$refs.chartSvg && this.$refs.chartContainer && (this.viewMode === 'chart' || this.viewMode === 'diff')) {
        this.renderChartToContainer(this.$refs.chartContainer, this.$refs.chartSvg, this.$refs.chartTooltip);
      }
      
      // Also render to split chart if in split mode
      if (this.$refs.splitChartSvg && this.$refs.splitChartContainer && this.viewMode === 'split') {
        this.renderChartToContainer(this.$refs.splitChartContainer, this.$refs.splitChartSvg, this.$refs.splitChartTooltip);
      }
      
      // Also render to chartgrid chart if in chartgrid mode
      if (this.$refs.chartgridChartSvg && this.$refs.chartgridChartContainer && this.viewMode === 'chartgrid') {
        this.renderChartToContainer(this.$refs.chartgridChartContainer, this.$refs.chartgridChartSvg, this.$refs.chartgridChartTooltip);
      }
    },
    
    renderChartToContainer(container, svgElement, tooltipElement) {
      const svg = d3.select(svgElement);
      const tooltip = d3.select(tooltipElement);
      
      // Clear previous
      svg.selectAll('*').remove();
      
      // Dimensions
      const margin = { top: 20, right: 30, bottom: 30, left: 50 };
      const width = container.clientWidth - margin.left - margin.right;
      const containerHeight = container.clientHeight || 350;
      const height = containerHeight - margin.top - margin.bottom;
      
      svg.attr('width', width + margin.left + margin.right)
         .attr('height', height + margin.top + margin.bottom);
      
      const g = svg.append('g')
        .attr('transform', `translate(${margin.left},${margin.top})`);
      
      // Prepare data
      const element = this.selectedElement;
      const allData = [];
      
      this.selectedModelConfigs.forEach(config => {
        if (!config) return;
        const modelId = config.id;
        const modelData = this.queryData[modelId];
        if (!modelData || !modelData.days) return;
        
        const points = [];
        const spreadPoints = []; // For spread range (value ± std dev)
        
        modelData.days.forEach(day => {
          if (this.timeResolution === 'minutes' && day.hours) {
            day.hours.forEach(hour => {
              if (hour.minutes) {
                hour.minutes.forEach(min => {
                  const value = getRecordValue(min, element);
                  if (value !== undefined && value !== null) {
                    const baseValue = this.viewMode === 'diff' && this.baselineModel 
                      ? this.getBaselineValue(min.datetimeEpoch, element) || 0
                      : 0;
                    
                    let source = null;
                    let allSources = null;
                    if (this.optionTrackModelChanges && min.stations) {
                      const sources = this.parseStationsSources(min.stations);
                      if (sources) {
                        source = sources[element] || null;
                        allSources = sources;
                      }
                    }
                    
                    points.push({
                      datetime: min.datetimeEpoch * 1000,
                      value: value - baseValue,
                      source: source,
                      allSources: allSources
                    });
                    
                    if (this.showEnsembleSpread) {
                      const spreadVal = getSpreadValue(min, element);
                      if (spreadVal !== undefined) {
                        spreadPoints.push({
                          datetime: min.datetimeEpoch * 1000,
                          min: (value - spreadVal) - baseValue,
                          max: (value + spreadVal) - baseValue,
                          value: value - baseValue,
                          spread: spreadVal
                        });
                      }
                    }
                  }
                });
              }
            });
          } else if (this.timeResolution === 'hours' && day.hours) {
            day.hours.forEach(hour => {
              const value = getRecordValue(hour, element);
              if (value !== undefined && value !== null) {
                const baseValue = this.viewMode === 'diff' && this.baselineModel 
                  ? this.getBaselineValue(hour.datetimeEpoch, element) || 0
                  : 0;
                
                // Parse source from stations field if trackModelChanges enabled
                let source = null;
                let allSources = null;
                if (this.optionTrackModelChanges && hour.stations) {
                  const sources = this.parseStationsSources(hour.stations);
                  if (sources) {
                    source = sources[element] || null;
                    allSources = sources;
                  }
                }
                
                points.push({
                  datetime: hour.datetimeEpoch * 1000,
                  value: value - baseValue,
                  source: source,
                  allSources: allSources
                });
                
                // Collect spread data if available (uses hour.spread sub-object)
                if (this.showEnsembleSpread) {
                  const spreadVal = getSpreadValue(hour, element);
                  if (spreadVal !== undefined) {
                    spreadPoints.push({
                      datetime: hour.datetimeEpoch * 1000,
                      min: (value - spreadVal) - baseValue,
                      max: (value + spreadVal) - baseValue,
                      value: value - baseValue,
                      spread: spreadVal
                    });
                  }
                }
              }
            });
          } else {
            const value = getRecordValue(day, element);
            if (value !== undefined && value !== null) {
              const baseValue = this.viewMode === 'diff' && this.baselineModel
                ? this.getBaselineValue(day.datetimeEpoch, element) || 0
                : 0;
            
              // Parse source from stations field if trackModelChanges enabled
              let source = null;
              let allSources = null;
              if (this.optionTrackModelChanges && day.stations) {
                const sources = this.parseStationsSources(day.stations);
                if (sources) {
                  source = sources[element] || null;
                  allSources = sources;
                }
              }
            
              points.push({
                datetime: day.datetimeEpoch * 1000,
                value: value - baseValue,
                source: source,
                allSources: allSources
              });
            
              // Collect spread data if available
              if (this.showEnsembleSpread) {
                const spreadVal = getSpreadValue(day, element);
                if (spreadVal !== undefined) {
                  spreadPoints.push({
                    datetime: day.datetimeEpoch * 1000,
                    min: (value - spreadVal) - baseValue,
                    max: (value + spreadVal) - baseValue,
                    value: value - baseValue,
                    spread: spreadVal
                  });
                }
              }
            }
          }
        });
        
        if (points.length > 0) {
          const hasSpread = spreadPoints.length > 0;
          allData.push({
            modelId,
            model: config,
            points,
            spreadPoints: hasSpread ? spreadPoints : null,
            hasSpread
          });
        }
      });
      
      if (allData.length === 0) return;
      
      // Scales - include ensemble spread in extent calculation
      const xExtent = d3.extent(allData.flatMap(d => d.points.map(p => p.datetime)));
      let yValues = allData.flatMap(d => d.points.map(p => p.value));
      
      // Include ensemble spread min/max in y extent
      allData.forEach(d => {
        if (d.spreadPoints && d.spreadPoints.length > 0) {
          d.spreadPoints.forEach(sp => {
            yValues.push(sp.min, sp.max);
          });
        }
      });
      
      const yExtent = d3.extent(yValues);
      const yPadding = (yExtent[1] - yExtent[0]) * 0.1;
      
      const x = d3.scaleTime()
        .domain(xExtent)
        .range([0, width]);
      
      const y = d3.scaleLinear()
        .domain([yExtent[0] - yPadding, yExtent[1] + yPadding])
        .range([height, 0]);
      
      // Zero line for diff mode
      if (this.viewMode === 'diff') {
        g.append('line')
          .attr('x1', 0)
          .attr('x2', width)
          .attr('y1', y(0))
          .attr('y2', y(0))
          .attr('stroke', '#999')
          .attr('stroke-dasharray', '5,5');
      }
      
      // Ensemble spread areas (render first, so lines are on top)
      const area = d3.area()
        .defined(d => d.min !== null && d.max !== null)
        .x(d => x(d.datetime))
        .y0(d => y(d.min))
        .y1(d => y(d.max))
        .curve(d3.curveMonotoneX);
      
      allData.forEach(data => {
        if (data.spreadPoints && data.spreadPoints.length > 0 && this.showEnsembleSpread) {
          g.append('path')
            .datum(data.spreadPoints)
            .attr('class', `ensemble-spread ensemble-${data.modelId}`)
            .attr('fill', data.model?.color || '#888')
            .attr('fill-opacity', this.ensembleSpreadOpacity)
            .attr('stroke', 'none')
            .attr('d', area);
          
          // Add a label for the spread
          const lastPoint = data.spreadPoints[data.spreadPoints.length - 1];
          if (lastPoint) {
            g.append('text')
              .attr('class', 'ensemble-label')
              .attr('x', x(lastPoint.datetime) + 5)
              .attr('y', y((lastPoint.min + lastPoint.max) / 2))
              .attr('fill', data.model?.color || '#888')
              .attr('font-size', '10px')
              .attr('dominant-baseline', 'middle')
              .text(`±${((lastPoint.max - lastPoint.min) / 2).toFixed(1)}`);
          }
        }
      });
      
      // Determine time gap threshold based on resolution
      // Gap is considered "large" if more than 1 interval (1 minute, 1 hour or 1 day)
      const gapThreshold = this.timeResolution === 'minutes'
        ? (this.minuteInterval || 15) * 60 * 1000 * 1.5  // 1.5x minute interval in ms
        : this.timeResolution === 'hours' 
        ? 60 * 60 * 1000 * 1.5   // 1.5 hours in ms
        : 24 * 60 * 60 * 1000 * 1.5; // 1.5 days in ms
      
      // Helper function to segment points based on time gaps
      const segmentPoints = (points) => {
        if (points.length < 2) return { continuous: [points], gaps: [] };
        
        const continuous = [];
        const gaps = [];
        let currentSegment = [points[0]];
        
        for (let i = 1; i < points.length; i++) {
          const timeDiff = points[i].datetime - points[i - 1].datetime;
          
          if (timeDiff > gapThreshold) {
            // Large gap - end current segment, record gap
            if (currentSegment.length > 0) {
              continuous.push(currentSegment);
            }
            gaps.push([points[i - 1], points[i]]); // Gap connection
            currentSegment = [points[i]];
          } else {
            // Continuous - add to current segment
            currentSegment.push(points[i]);
          }
        }
        
        if (currentSegment.length > 0) {
          continuous.push(currentSegment);
        }
        
        return { continuous, gaps };
      };
      
      // Line generator for continuous segments
      const line = d3.line()
        .defined(d => d.value !== null)
        .x(d => x(d.datetime))
        .y(d => y(d.value))
        .curve(d3.curveMonotoneX);
      
      // Line generator for gap segments (straight lines)
      const gapLine = d3.line()
        .x(d => x(d.datetime))
        .y(d => y(d.value));
      
      allData.forEach(data => {
        const { continuous, gaps } = segmentPoints(data.points);
        const baseColor = data.model?.color || '#888';
        const baseWidth = data.hasSpread ? 2.5 : (data.model?.width || 1.5);
        const isObs = data.model?.isObservation;
        
        // Draw continuous line segments (subtle, faded — observations are bolder)
        continuous.forEach(segment => {
          if (segment.length >= 2) {
            g.append('path')
              .datum(segment)
              .attr('class', `model-line model-${data.modelId}`)
              .attr('fill', 'none')
              .attr('stroke', baseColor)
              .attr('stroke-width', isObs ? baseWidth * 1.2 : baseWidth * 0.8)
              .attr('stroke-opacity', isObs ? 0.8 : 0.4)
              .attr('d', line);
          }
        });
        
        // Draw gap segments (dotted, even more subtle)
        gaps.forEach(gapPair => {
          g.append('path')
            .datum(gapPair)
            .attr('class', `model-line-gap model-${data.modelId}`)
            .attr('fill', 'none')
            .attr('stroke', baseColor)
            .attr('stroke-width', baseWidth * 0.6)
            .attr('stroke-opacity', 0.25)
            .attr('stroke-dasharray', '4,4')
            .attr('d', gapLine);
        });
        
        // Draw data points — observations use diamonds, models use circles
        if (isObs) {
          const diamondSize = 5;
          g.selectAll(`.data-point-${data.modelId}`)
            .data(data.points.filter(p => p.value !== null))
            .enter()
            .append('path')
            .attr('class', `data-point data-point-${data.modelId}`)
            .attr('d', d3.symbol().type(d3.symbolDiamond).size(diamondSize * diamondSize * 2))
            .attr('transform', d => `translate(${x(d.datetime)},${y(d.value)})`)
            .attr('fill', baseColor)
            .attr('stroke', '#fff')
            .attr('stroke-width', 1.5)
            .attr('opacity', 0.95);
        } else {
          g.selectAll(`.data-point-${data.modelId}`)
            .data(data.points.filter(p => p.value !== null))
            .enter()
            .append('circle')
            .attr('class', `data-point data-point-${data.modelId}`)
            .attr('cx', d => x(d.datetime))
            .attr('cy', d => y(d.value))
            .attr('r', 4)
            .attr('fill', baseColor)
            .attr('stroke', '#fff')
            .attr('stroke-width', 1.5)
            .attr('opacity', 0.9);
        }
      });
      
      // Axes - use adaptive tick count to avoid label overlap
      const tickFmt = (this.timeResolution === 'minutes' || this.timeResolution === 'hours') ? d3.timeFormat('%d %H:%M') : d3.timeFormat('%m/%d');
      const maxTicks = Math.max(4, Math.floor(width / 80));
      g.append('g')
        .attr('transform', `translate(0,${height})`)
        .call(d3.axisBottom(x).ticks(maxTicks).tickFormat(tickFmt))
        .selectAll('text')
        .attr('transform', 'rotate(-30)')
        .style('text-anchor', 'end')
        .style('font-size', '10px');
      
      g.append('g')
        .call(d3.axisLeft(y).ticks(6));
      
      // Tooltip interaction
      const me = this;
      const focus = g.append('line')
        .attr('class', 'focus-line')
        .attr('y1', 0)
        .attr('y2', height)
        .attr('stroke', '#999')
        .attr('stroke-dasharray', '3,3')
        .style('opacity', 0);
      
      svg.on('mousemove', function(event) {
        const [mx] = d3.pointer(event);
        let xVal = x.invert(mx - margin.left);
        
        // Snap to nearest time interval based on resolution
        if (me.timeResolution === 'minutes') {
          const minMs = (me.minuteInterval || 15) * 60 * 1000;
          xVal = new Date(Math.round(xVal.getTime() / minMs) * minMs);
        } else if (me.timeResolution === 'hours') {
          const hourMs = 60 * 60 * 1000;
          xVal = new Date(Math.round(xVal.getTime() / hourMs) * hourMs);
        } else {
          // Daily - snap to nearest actual data point time
          // The API returns datetimeEpoch at local midnight which may not be UTC midnight,
          // so we find the closest data point rather than rounding to UTC midnight
          const allPoints = allData.flatMap(d => d.points);
          if (allPoints.length > 0) {
            let closestTime = allPoints[0].datetime;
            let closestDist = Math.abs(xVal.getTime() - closestTime);
            for (const p of allPoints) {
              const dist = Math.abs(xVal.getTime() - p.datetime);
              if (dist < closestDist) {
                closestDist = dist;
                closestTime = p.datetime;
              }
            }
            xVal = new Date(closestTime);
          }
        }
        
        // Update focus line position to snapped time
        const snappedX = x(xVal);
        focus.attr('x1', snappedX)
             .attr('x2', snappedX)
             .style('opacity', 1);
        
        // Build tooltip content - use appropriate format for resolution
        const timeFormat = (me.timeResolution === 'minutes' || me.timeResolution === 'hours') ? '%b %d, %H:%M' : '%b %d';
        const utcTimeStr = (me.timeResolution === 'minutes' || me.timeResolution === 'hours') 
          ? xVal.toISOString().slice(0, 16).replace('T', ' ') + ' UTC'
          : xVal.toISOString().slice(0, 10) + ' UTC';
        let html = `<div class="tooltip-time">${d3.timeFormat(timeFormat)(xVal)}</div>`;
        html += `<div class="tooltip-utc">${utcTimeStr}</div>`;
        
        // Only show values for actual data points - not interpolated
        // Exact match threshold: within 30s for minutes, within 1 minute for hourly, within 12 hours for daily
        // (daily points use local midnight which can differ significantly from UTC midnight)
        const exactMatchThreshold = me.timeResolution === 'minutes' ? 30 * 1000 : me.timeResolution === 'hours' ? 60 * 1000 : 12 * 60 * 60 * 1000;
        
        allData.forEach(data => {
          if (!data.points || data.points.length === 0) {
            // No data for this model
            html += `<div class="tooltip-row">
              <span class="tooltip-color" style="background:${data.model?.color || '#888'}"></span>
              <span class="tooltip-name">${data.model?.name || data.modelId}</span>
              <span class="tooltip-value">—</span>
            </div>`;
            return;
          }
          
          // Find an exact match at the snapped time
          const exactPoint = data.points.find(p => Math.abs(p.datetime - xVal.getTime()) < exactMatchThreshold);
          
          // Only show value if there's an actual data point at this time
          const hasDataAtTime = !!exactPoint;
          
          // Check for spread data (from hour.spread sub-object)
          let spreadInfo = '';
          if (hasDataAtTime && data.spreadPoints) {
            const exactSpread = data.spreadPoints.find(p => Math.abs(p.datetime - xVal.getTime()) < exactMatchThreshold);
            if (exactSpread && exactSpread.spread !== undefined) {
              spreadInfo = `<span class="tooltip-spread">(\u00b1${me.formatValue(exactSpread.spread)})</span>`;
            }
          }
          
          const spreadIcon = data.hasSpread ? '<i class=\"bi bi-graph-up\" style=\"font-size:9px;margin-right:2px;\"></i>' : '';
          const displayValue = hasDataAtTime ? `${me.formatValue(exactPoint.value)} ${spreadInfo}` : '\u2014';
          
          // Show all model sources if trackModelChanges is enabled
          let sourcesHtml = '';
          if (hasDataAtTime && exactPoint.allSources) {
            const src = exactPoint.allSources[me.selectedElement];
            if (src) {
              sourcesHtml = `<div class="tooltip-source" title="${src}">${src}</div>`;
            }
          } else if (hasDataAtTime && exactPoint.source) {
            sourcesHtml = `<div class="tooltip-source" title="${exactPoint.source}">${exactPoint.source}</div>`;
          }
          
          html += `<div class="tooltip-row ${data.hasSpread ? 'has-spread' : ''}">
            <span class="tooltip-color" style="background:${data.model?.color || '#888'}"></span>
            <span class="tooltip-name">${spreadIcon}${data.model?.name || data.modelId}</span>
            <span class="tooltip-value">${displayValue}</span>
            ${sourcesHtml}
          </div>`;
        });
        
        tooltip.select('.tooltip-content').html(html);
        tooltip.style('opacity', 1)
               .style('left', `${mx + 10}px`)
               .style('top', '20px');
      });
      
      // Click to sync map time (only in split mode)
      svg.on('click', function(event) {
        if (me.viewMode !== 'split') return;
        
        const [mx] = d3.pointer(event);
        let xVal = x.invert(mx - margin.left);
        
        // Snap to nearest time interval based on resolution
        if (me.timeResolution === 'minutes') {
          const minMs = (me.minuteInterval || 15) * 60 * 1000;
          xVal = new Date(Math.round(xVal.getTime() / minMs) * minMs);
        } else if (me.timeResolution === 'hours') {
          const hourMs = 60 * 60 * 1000;
          xVal = new Date(Math.round(xVal.getTime() / hourMs) * hourMs);
        } else {
          // Daily - snap to nearest actual data point
          const allPoints = allData.flatMap(d => d.points);
          if (allPoints.length > 0) {
            let closestTime = allPoints[0].datetime;
            let closestDist = Math.abs(xVal.getTime() - closestTime);
            for (const p of allPoints) {
              const dist = Math.abs(xVal.getTime() - p.datetime);
              if (dist < closestDist) {
                closestDist = dist;
                closestTime = p.datetime;
              }
            }
            xVal = new Date(closestTime);
          }
        }
        
        // Sync the map time
        me.syncMapTime(xVal);
      });
      
      svg.on('mouseleave', function() {
        focus.style('opacity', 0);
        tooltip.style('opacity', 0);
      });
    },
    
    // Sync the map to a specific time from chart interaction
    syncMapTime(dateTime) {
      if (!dateTime) return;
      const d = (dateTime instanceof Date) ? dateTime : new Date(dateTime * 1000);
      this.setMapDateTime(d);
    },
    
    getBaselineValue(datetimeEpoch, element) {
      if (!this.baselineModel || !this.queryData[this.baselineModel]) return null;
      return this.getValueAtTime(this.queryData[this.baselineModel], datetimeEpoch, element);
    }
  },

  watch: {
    forecastBasisDate(newDate, oldDate) {
      // When user picks a new date and hasn't manually set a time, default to 00:00 (00Z)
      if (newDate && newDate !== oldDate && !this.forecastBasisUserSetTime) {
        this.forecastBasisTime = '00:00';
      }
      // Sync start date to match the new forecast basis date
      if (newDate && newDate !== oldDate) {
        this.startDate = newDate;
      }
    },
    startDate(newStart, oldStart) {
      // Maintain the same interval between start and end dates
      if (newStart && oldStart && newStart !== oldStart) {
        const oldStartMs = new Date(oldStart).getTime();
        const oldEndMs = new Date(this.endDate).getTime();
        const interval = oldEndMs - oldStartMs;
        const newEnd = new Date(new Date(newStart).getTime() + interval);
        this.endDate = newEnd.toISOString().split('T')[0];
      }
    },
    forecastBasisTime(newTime, oldTime) {
      // Mark that user has explicitly set the time
      if (newTime !== oldTime) {
        this.forecastBasisUserSetTime = true;
      }
    },
    queryData: {
      handler() {
        this.$nextTick(() => this.renderChart());
      },
      deep: true
    },
    selectedElement() {
      this.$nextTick(() => this.renderChart());
      this.syncMapLayerToElement();
    },
    viewMode(newMode, oldMode) {
      // Destroy maps when leaving map/split views
      if (oldMode === 'map' && newMode !== 'map') {
        this.mapStopPlaying();
        this.destroyMap('leafletMap', 'wxTileLayer', 'circleMarker');
      }
      if (oldMode === 'split' && newMode !== 'split') {
        this.mapStopPlaying();
        this.destroyMap('splitLeafletMap', 'splitWxTileLayer', 'splitCircleMarker');
      }
      this.$nextTick(() => {
        // Initialize map when entering map or split view
        if (newMode === 'map' || newMode === 'split') {
          this.$nextTick(() => this.initializeMapView());
        }
        // Render chart - use double nextTick for v-if views since they need to mount first
        if (newMode === 'split' || newMode === 'chartgrid') {
          this.$nextTick(() => this.renderChart());
        } else {
          this.renderChart();
        }
      });
    },
    timeResolution() {
      // If current element is daily-only and we switched to hourly, fall back to the base element
      const dailyToHourlyMap = { 'tempmax': 'temp', 'tempmin': 'temp', 'feelslikemax': 'feelslike', 'feelslikemin': 'feelslike' };
      if ((this.timeResolution === 'hours' || this.timeResolution === 'minutes') && dailyToHourlyMap[this.selectedElement]) {
        this.selectedElement = dailyToHourlyMap[this.selectedElement];
      }
      this.saveConfig();
    },
    showEnsembleSpread() {
      this.$nextTick(() => this.renderChart());
    },
    ensembleSpreadOpacity() {
      this.$nextTick(() => this.renderChart());
    },
    location: {
      handler() {
        this.syncMapLocation();
      },
      deep: true
    },
    selectedModels: {
      handler() {
        this.saveConfig();
      },
      deep: true
    },
    selectedElement() { this.saveConfig(); },
    elementsString() { this.saveConfig(); },
    minuteInterval() { this.saveConfig(); },
    unitGroup() { this.saveConfig(); },
    viewMode() { this.saveConfig(); },
    baselineModel() {
      this.saveConfig();
      this.$nextTick(() => this.renderChart());
    },
    locationText() { this.saveConfig(); },
    optionNointerpolate() { this.saveConfig(); },
    optionAttribution() { this.saveConfig(); },
    optionUsefcst() { this.saveConfig(); },
    optionTrackModelChanges() { this.saveConfig(); },
    optionCompareWithObs() { this.saveConfig(); },
    modelPanelCollapsed() { this.saveConfig(); },
    statsPanelCollapsed() { this.saveConfig(); },
    initialLocation: {
      handler(val) {
        if (val) {
          this.location = val;
          this.locationText = val.name || `${val.lat}, ${val.lon}`;
        }
      },
      immediate: true
    }
  },

  mounted() {
    window.addEventListener('resize', this.renderChart);
    
    // Restore persisted configuration before anything else
    this.restoreConfig();

    // Initialize map if starting in map/split view (watcher only fires on change)
    if (this.viewMode === 'map' || this.viewMode === 'split') {
      this.$nextTick(() => this.initializeMapView());
    }
    
    // Initialize session state from vcCommunicator
    const vcCommunicator = window.vcCommunicator;
    if (vcCommunicator) {
      this.sessionId = vcCommunicator.sessionId;
      this.sessionState = vcCommunicator.sessionState;
      
      // Listen for session creation
      vcCommunicator.addHandler('createsession', (sessionId, sessionState) => {
        this.sessionId = sessionId;
        this.sessionState = sessionState;
        // Load V2 models when session is available
        this.loadV2Models();
      });
      
      // Listen for session termination
      vcCommunicator.addHandler('terminatesession', () => {
        this.sessionId = null;
        this.sessionState = null;
      });
      
      // Load V2 models if we already have an API key
      if (this.weatherApiKey) {
        this.loadV2Models();
      }
    }
  },

  beforeUnmount() {
    window.removeEventListener('resize', this.renderChart);
    // Clean up split resize listeners if still active
    document.removeEventListener('mousemove', this.onSplitResize);
    document.removeEventListener('mouseup', this.stopSplitResize);
    document.removeEventListener('touchmove', this.onSplitResize);
    document.removeEventListener('touchend', this.stopSplitResize);
    // Clean up Leaflet maps
    this.mapStopPlaying();
    this.destroyMap('leafletMap', 'wxTileLayer', 'circleMarker');
    this.destroyMap('splitLeafletMap', 'splitWxTileLayer', 'splitCircleMarker');
  }
};
</script>

<style scoped>
/* Bootstrap Icons spin animation */
.spin-icon {
  animation: spin-animation 1s linear infinite;
}

@keyframes spin-animation {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* Large icon sizing (replaces fa-3x) */
.icon-3x {
  font-size: 3rem;
}

.model-comparison {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #f8f9fa;
  font-size: 13px;
}

/* Header */
.comparison-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: linear-gradient(135deg, #1a365d 0%, #2d4a7c 100%);
  color: white;
}

.comparison-header h4 {
  font-size: 16px;
  font-weight: 600;
}

.header-actions .btn {
  color: white;
  border-color: rgba(255,255,255,0.3);
}

/* Controls Bar */
.controls-bar {
  display: flex;
  flex-wrap: wrap;
  gap: 10px 14px;
  padding: 8px 12px;
  background: white;
  border-bottom: 1px solid #dee2e6;
  align-items: flex-end;
}

.control-group {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.control-label {
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  color: #6c757d;
  line-height: 1;
}

.api-key-status {
  font-size: 11px;
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 4px;
}

.api-key-status .bi-key-fill {
  font-size: 10px;
}

.location-input-wrapper {
  display: flex;
  gap: 4px;
}

.location-input {
  min-width: 180px;
}

.location-display {
  font-size: 10px;
  color: #28a745;
  line-height: 1.2;
}

.date-inputs {
  display: flex;
  align-items: center;
  gap: 4px;
}

.forecast-time-input {
  width: 85px !important;
  flex-shrink: 0;
}

.tz-select {
  width: auto;
  min-width: 60px;
}

.unit-select {
  width: auto;
  min-width: 62px;
  border-radius: 0 4px 4px 0 !important;
}

.minute-interval-select {
  width: auto;
  min-width: 62px;
  border-radius: 0 !important;
}

.date-separator {
  color: #6c757d;
}

.quick-dates {
  display: flex;
  gap: 6px;
  margin-top: 1px;
}

.quick-date-link {
  font-size: 10px;
  color: #0d6efd;
  text-decoration: none;
  cursor: pointer;
}

.quick-date-link:hover {
  text-decoration: underline;
  color: #0a58ca;
}

/* Forecast basis hint */
.forecast-basis-hint {
  font-size: 10px;
  color: #856404;
  background: #fff3cd;
  border-radius: 3px;
  padding: 1px 5px;
  margin-top: 2px;
}

.forecast-basis-hint .bi {
  margin-right: 2px;
}

/* Disabled option checkbox */
.option-checkbox.disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Out of bounds models */
.model-checkbox.out-of-bounds {
  opacity: 0.6;
}

.model-checkbox.out-of-bounds .model-name {
  text-decoration: line-through;
  color: #999;
}

.out-bounds-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  color: #dc3545;
  font-size: 12px;
  flex-shrink: 0;
}

/* API status group */
.fetch-group {
  display: flex;
  flex-direction: row !important;
  align-items: center;
  gap: 6px;
  margin-left: auto;
}

.btn-xs {
  padding: 2px 6px;
  font-size: 11px;
}

.fetch-btn {
  min-width: 100px;
  transition: background-color 0.2s, border-color 0.2s;
}

.fetch-btn.btn-warning {
  animation: pulse-warning 1.5s ease-in-out infinite;
}

@keyframes pulse-warning {
  0%, 100% { box-shadow: 0 0 0 0 rgba(255, 193, 7, 0.4); }
  50% { box-shadow: 0 0 0 4px rgba(255, 193, 7, 0); }
}

/* Main Content */
.comparison-content {
  display: flex;
  flex: 1;
  overflow: hidden;
}

/* Model Panel */
.model-panel {
  width: 220px;
  background: white;
  border-right: 1px solid #dee2e6;
  display: flex;
  flex-direction: column;
  transition: width 0.2s;
}

.model-panel.collapsed {
  width: 40px;
}

.panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  background: #f1f3f5;
  border-bottom: 1px solid #dee2e6;
  cursor: pointer;
  font-weight: 600;
}

.selected-count {
  font-size: 11px;
  color: #6c757d;
  margin-right: auto;
  margin-left: 8px;
}

.panel-content {
  flex: 1;
  overflow-y: auto;
  padding: 8px;
}

.model-presets {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-bottom: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid #dee2e6;
}

.model-group {
  margin-bottom: 8px;
}

.group-header {
  display: flex;
  align-items: center;
  padding: 6px 8px;
  background: #e9ecef;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
  font-size: 12px;
}

.group-header:hover {
  background: #dee2e6;
}

.group-models {
  padding: 6px 0 6px 16px;
}

.v2-quick-buttons {
  display: flex;
  gap: 4px;
  margin-bottom: 6px;
}

.v2-quick-buttons .btn {
  font-size: 10px;
  padding: 2px 6px;
  line-height: 1.3;
}

.model-checkbox {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 0;
  cursor: pointer;
  font-size: 12px;
}

.model-checkbox input {
  margin: 0;
}

.model-color {
  width: 12px;
  height: 12px;
  border-radius: 2px;
}

.model-badge {
  font-size: 9px;
  padding: 1px 4px;
  background: #17a2b8;
  color: white;
  border-radius: 3px;
  margin-left: auto;
}

.badge-v2 {
  background: #28a745;
  font-weight: 600;
}

.badge-legacy {
  background: #dc3545;
  font-weight: normal;
}

/* V2/Legacy group headers */
.v2-header {
  background: rgba(40, 167, 69, 0.1);
  border-left: 3px solid #28a745;
}

.legacy-header {
  background: rgba(220, 53, 69, 0.1);
  border-left: 3px solid #dc3545;
}

.group-count {
  font-size: 11px;
  color: #6c757d;
  margin-left: 4px;
}

.loading-models {
  padding: 12px;
  text-align: center;
  color: #6c757d;
  font-size: 12px;
}

.no-models-msg {
  padding: 8px 12px;
  color: #6c757d;
  font-size: 11px;
  font-style: italic;
}

/* Legacy/V2 indicators in various places */
.legend-legacy, .th-legacy, .rank-legacy {
  color: #dc3545;
  font-size: 12px;
  margin-left: 2px;
}

.legend-v2, .th-v2, .rank-v2 {
  color: #28a745;
  font-size: 11px;
  font-weight: 600;
  margin-left: 2px;
}

.legacy-model {
  background: rgba(220, 53, 69, 0.1) !important;
}

.v2-model {
  background: rgba(40, 167, 69, 0.1) !important;
}

/* Results Area */
.results-area {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: white;
}

.element-selector {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border-bottom: 1px solid #dee2e6;
  flex-wrap: wrap;
}

.element-selector .form-select {
  width: auto;
  min-width: 150px;
}

.view-toggle {
  margin-left: auto;
}

/* Chart */
.chart-container {
  flex: 1;
  padding: 16px;
  overflow: hidden;
}

.comparison-chart {
  position: relative;
  width: 100%;
  height: 350px;
  background: #fff;
}

.comparison-chart svg {
  width: 100%;
  height: 100%;
}

.chart-tooltip {
  position: absolute;
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  padding: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.15);
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.15s;
  z-index: 100;
  min-width: 150px;
}

.tooltip-content :deep(.tooltip-time) {
  font-weight: 600;
  margin-bottom: 2px;
}

.tooltip-content :deep(.tooltip-utc) {
  font-size: 10px;
  color: #999;
  margin-bottom: 6px;
  padding-bottom: 6px;
  border-bottom: 1px solid #eee;
}

.tooltip-content :deep(.tooltip-row) {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 2px 0;
}

.tooltip-content :deep(.tooltip-color) {
  width: 10px;
  height: 10px;
  border-radius: 2px;
}

.tooltip-content :deep(.tooltip-value) {
  margin-left: auto;
  font-weight: 600;
}

.chart-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  padding: 8px 16px;
  border-top: 1px solid #eee;
}

/* Data point circles */
.data-point {
  transition: r 0.15s ease, opacity 0.15s ease;
  cursor: pointer;
}

.data-point:hover {
  r: 6;
  opacity: 1 !important;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  cursor: pointer;
  transition: opacity 0.2s;
}

.legend-item.dimmed {
  opacity: 0.3;
}

.model-checkbox.dimmed {
  opacity: 0.35;
}

.model-checkbox {
  transition: opacity 0.15s;
}

.legend-color {
  width: 16px;
  height: 3px;
  border-radius: 1px;
}

.legend-spread-indicator {
  font-size: 10px;
  color: #fd7e14;
  margin-left: 2px;
}

.legend-item.is-ensemble .legend-color {
  height: 8px;
  opacity: 0.7;
}

/* Table */
.table-container {
  flex: 1;
  overflow: auto;
  padding: 16px;
}

.table-container .table {
  font-size: 12px;
}

.sticky-col {
  position: sticky;
  left: 0;
  background: white;
  z-index: 1;
}

.cell-similar {
  background: rgba(40, 167, 69, 0.1) !important;
}

.cell-higher {
  background: rgba(220, 53, 69, 0.1) !important;
}

.cell-lower {
  background: rgba(0, 123, 255, 0.1) !important;
}

.cell-source {
  font-size: 9px;
  color: #17a2b8;
  font-weight: 600;
  line-height: 1.2;
  white-space: normal;
  word-break: break-all;
  max-width: 220px;
}

/* Empty/Loading States */
.empty-state,
.loading-state {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #6c757d;
  text-align: center;
  padding: 40px;
}

.empty-state i,
.loading-state i {
  color: #adb5bd;
}

.loading-state .progress {
  width: 200px;
  height: 8px;
}

/* Map and Split Empty State Overlays */
.map-empty-overlay,
.split-empty-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(248, 249, 250, 0.92);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  pointer-events: none;
}

.empty-state-content {
  pointer-events: auto;
  text-align: center;
  color: #6c757d;
  padding: 30px;
}

.empty-state-content i {
  color: #adb5bd;
}

.empty-state-content h5 {
  margin-bottom: 8px;
  color: #495057;
}

.empty-state-content p {
  margin: 0;
  font-size: 13px;
  max-width: 280px;
}

/* Stats Panel */
.stats-panel {
  width: 250px;
  background: white;
  border-left: 1px solid #dee2e6;
  display: flex;
  flex-direction: column;
  transition: width 0.2s;
}

.stats-panel.collapsed {
  width: 40px;
}

.stats-table {
  width: 100%;
  font-size: 11px;
  border-collapse: collapse;
}

.stats-table th,
.stats-table td {
  padding: 6px 8px;
  border-bottom: 1px solid #eee;
}

.stats-table th {
  text-align: left;
  font-weight: 600;
  background: #f8f9fa;
}

.stats-table .is-baseline {
  background: rgba(0, 123, 255, 0.1);
}

.stats-table tr.is-highlighted {
  background: rgba(0, 123, 255, 0.15);
}

.stats-table tr.dimmed {
  opacity: 0.4;
}

.stats-table .is-min {
  color: #007bff;
  font-weight: 600;
}

.stats-table .is-max {
  color: #dc3545;
  font-weight: 600;
}

.stats-table .response-time {
  color: #6c757d;
  font-size: 10px;
  white-space: nowrap;
}

.stat-color {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 2px;
  margin-right: 4px;
  vertical-align: middle;
}

.spread-analysis {
  padding: 12px;
  margin: 12px 8px;
  background: #f8f9fa;
  border-radius: 4px;
}

.spread-analysis h6 {
  font-size: 12px;
  margin-bottom: 8px;
}

.spread-item {
  display: flex;
  justify-content: space-between;
  padding: 4px 0;
  font-size: 12px;
}

.model-rankings {
  padding: 12px;
  margin: 12px 8px;
  background: #f8f9fa;
  border-radius: 4px;
}

.obs-accuracy {
  padding: 12px;
  margin: 12px 8px;
  background: #f8f9fa;
  border-radius: 4px;
}

.obs-accuracy h6 {
  font-size: 12px;
  margin-bottom: 8px;
}

.obs-accuracy-table tr.is-best td {
  font-weight: 700;
}

.obs-accuracy-note {
  font-size: 10px;
  color: #6c757d;
  margin-top: 6px;
  text-align: right;
}

/* Options group */
.options-group {
  align-self: center;
}

/* Option checkboxes */
.option-checkboxes {
  display: grid;
  grid-template-columns: repeat(3, auto);
  gap: 3px 6px;
}

.option-checkbox {
  display: flex;
  align-items: center;
  gap: 3px;
  font-size: 10px;
  cursor: pointer;
  padding: 1px 5px;
  border-radius: 3px;
  background: #f0f0f0;
  transition: background 0.15s;
  white-space: nowrap;
}

.option-checkbox:hover {
  background: #e0e0e0;
}

.option-checkbox input {
  margin: 0;
  cursor: pointer;
  width: 12px;
  height: 12px;
}

.option-checkbox span {
  color: #555;
}

/* Attribution info */
.attribution-info {
  padding: 12px;
  margin: 12px 8px;
  background: #f0f8ff;
  border-radius: 4px;
  border-left: 3px solid #2196F3;
}

.attribution-info h6 {
  font-size: 12px;
  margin-bottom: 8px;
  color: #1565c0;
}

.attribution-list {
  font-size: 11px;
}

.attribution-item {
  margin-bottom: 8px;
}

.attribution-item:last-child {
  margin-bottom: 0;
}

.attribution-model {
  font-weight: 600;
  color: #333;
  margin-bottom: 2px;
}

.attribution-detail {
  display: flex;
  gap: 8px;
  padding-left: 8px;
  color: #666;
}

.attr-model {
  font-weight: 500;
}

.attr-run {
  color: #888;
  font-family: monospace;
  font-size: 10px;
}

.model-rankings h6 {
  font-size: 12px;
  margin-bottom: 8px;
}

.ranking-item {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 0;
  font-size: 12px;
}

.rank-color {
  width: 8px;
  height: 8px;
  border-radius: 2px;
}

.rank-name {
  flex: 1;
}

.rank-deviation {
  font-weight: 600;
}

/* API Query Links */
.query-links-section {
  padding: 8px;
  margin: 12px 8px;
  background: #f5f5f5;
  border-radius: 4px;
  border-left: 3px solid #78909c;
}

.query-links-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  user-select: none;
}

.query-links-header h6 {
  font-size: 12px;
  margin: 0;
  color: #546e7a;
}

.query-links-header > i {
  font-size: 11px;
  color: #90a4ae;
}

.query-links-list {
  margin-top: 6px;
}

.query-link-item {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 3px 0;
  font-size: 11px;
  min-width: 0;
}

.query-link-item a {
  color: #1565c0;
  text-decoration: none;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
  min-width: 0;
}

.query-link-item a:hover {
  text-decoration: underline;
  color: #0d47a1;
}

.btn-copy-query {
  padding: 1px 4px;
  font-size: 10px;
  color: #78909c;
  border: none;
  background: none;
  flex-shrink: 0;
  cursor: pointer;
}

.btn-copy-query:hover {
  color: #37474f;
}

/* Modal */
.modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal.show {
  display: flex;
}

.modal-dialog {
  max-width: 500px;
  width: 90%;
}

.modal-content {
  background: white;
  border-radius: 8px;
  overflow: hidden;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
}

.modal-body {
  padding: 16px;
  max-height: 60vh;
  overflow-y: auto;
}

.modal-body h6 {
  margin-top: 16px;
  margin-bottom: 8px;
}

.modal-body h6:first-child {
  margin-top: 0;
}

/* Responsive */
@media (max-width: 1200px) {
  .model-panel {
    width: 180px;
  }
  .stats-panel {
    width: 200px;
  }
}

@media (max-width: 992px) {
  .comparison-content {
    flex-direction: column;
  }
  
  .model-panel,
  .stats-panel {
    width: 100%;
    max-height: 200px;
    border-right: none;
    border-left: none;
    border-bottom: 1px solid #dee2e6;
  }
  
  .model-panel.collapsed,
  .stats-panel.collapsed {
    max-height: 40px;
    width: 100%;
  }
}

/* Ensemble-specific styles */
.ensemble-badge {
  background: #fd7e14 !important;
  font-size: 8px;
}

.ensemble-options {
  padding: 8px;
  margin-top: 8px;
  background: #fff3cd;
  border-radius: 4px;
  border: 1px solid #ffc107;
}

.ensemble-options .form-check {
  margin-bottom: 6px;
}

.ensemble-options .form-check-label {
  font-size: 11px;
}

.opacity-slider {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 11px;
}

.opacity-slider input[type="range"] {
  width: 80px;
  height: 4px;
}

/* Ensemble tooltip styles */
.tooltip-content :deep(.tooltip-row.is-ensemble) {
  background: rgba(253, 126, 20, 0.1);
  padding: 2px 4px;
  border-radius: 3px;
  margin: 2px -4px;
}

.tooltip-content :deep(.tooltip-spread) {
  font-size: 10px;
  color: #6c757d;
  display: block;
  margin-top: 1px;
}

.tooltip-content :deep(.tooltip-source) {
  font-size: 9px;
  color: #17a2b8;
  font-weight: 600;
  margin-top: 1px;
  white-space: normal;
  word-break: break-all;
  max-width: 300px;
  line-height: 1.2;
  cursor: default;
}

.tooltip-content :deep(.tooltip-sources-grid) {
  margin-top: 2px;
  padding: 2px 0 2px 14px;
  border-left: 2px solid rgba(23,162,184,0.3);
  font-size: 9px;
  line-height: 1.35;
  max-width: 320px;
}

.tooltip-content :deep(.tooltip-src-row) {
  display: flex;
  gap: 4px;
  color: #6c757d;
}

.tooltip-content :deep(.tooltip-src-row.current) {
  color: #17a2b8;
  font-weight: 700;
}

.tooltip-content :deep(.src-el) {
  min-width: 50px;
  flex-shrink: 0;
}

.tooltip-content :deep(.src-val) {
  word-break: break-all;
}

/* Ensemble legend indicator */
.legend-item.is-ensemble .legend-color {
  height: 10px;
  border-radius: 3px;
  opacity: 0.6;
}

.legend-item.is-ensemble .legend-name::after {
  content: ' (ensemble)';
  font-size: 10px;
  color: #fd7e14;
}

/* Map View Styles – lightweight Leaflet */
.mc-map-view {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 400px;
}

.mc-map-wrap {
  position: relative;
  flex: 1;
  min-height: 350px;
  z-index: 0; /* contain Leaflet z-indexes */
  isolation: isolate;
}

.mc-map {
  width: 100%;
  height: 100%;
  position: absolute;
  inset: 0;
  z-index: 0;
}

.mc-map-layers {
  position: absolute;
  top: 10px;
  left: 10px;
  z-index: 800;
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
}

.mc-map-layer-btn {
  padding: 4px 10px;
  font-size: 11px;
  border: 1px solid rgba(0,0,0,.2);
  border-radius: 4px;
  background: #fff;
  color: #333;
  cursor: pointer;
  line-height: 1.3;
  white-space: nowrap;
}

.mc-map-layer-btn.mc-map-layer-btn--active {
  background: #0d6efd;
  color: #fff;
  border-color: #0d6efd;
}

.mc-map-model-picker {
  position: absolute;
  top: 10px;
  right: 50px;
  z-index: 800;
}

.mc-map-model-picker select {
  font-size: 11px;
  padding: 3px 6px;
  border: 1px solid rgba(0,0,0,.2);
  border-radius: 4px;
  background: #fff;
}

.mc-map-time {
  position: absolute;
  bottom: 10px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 800;
  display: flex;
  align-items: center;
  gap: 4px;
  background: rgba(255,255,255,.92);
  border: 1px solid rgba(0,0,0,.15);
  border-radius: 6px;
  padding: 4px 8px;
  font-size: 11px;
  backdrop-filter: blur(3px);
}

.mc-map-time-btn {
  padding: 2px 8px;
  font-size: 12px;
  border: 1px solid rgba(0,0,0,.15);
  border-radius: 4px;
  background: #fff;
  cursor: pointer;
  line-height: 1.3;
}

.mc-map-time-btn:hover { background: #e9ecef; }

.mc-map-time-display {
  padding: 0 6px;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  min-width: 110px;
  text-align: center;
}

.mc-map-empty {
  position: absolute;
  inset: 0;
  z-index: 900;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255,255,255,.75);
  font-size: 13px;
  color: #6c757d;
  pointer-events: none;
}

/* Split View Styles - Vertical layout (chart on top, map below) */
.split-view-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 600px;
  background: #dee2e6;
  overflow: hidden;
  position: relative;
}

.split-chart-section {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  background: white;
  min-height: 150px;
  overflow: hidden;
}

.split-chart-section .comparison-chart {
  height: 100%;
  min-height: 150px;
}

.split-chart-section .comparison-chart svg {
  width: 100%;
  height: 100%;
}

/* Split view resize handle */
.split-resize-handle {
  flex: 0 0 8px;
  background: #e9ecef;
  cursor: row-resize;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s ease;
  user-select: none;
  touch-action: none;
  position: relative;
  z-index: 100;
}

.split-resize-handle:hover {
  background: #dee2e6;
}

.split-resize-handle:active {
  background: #ced4da;
}

.resize-handle-bar {
  width: 40px;
  height: 4px;
  background: #adb5bd;
  border-radius: 2px;
  transition: background 0.15s ease;
}

.split-resize-handle:hover .resize-handle-bar {
  background: #6c757d;
}

.split-map-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  background: white;
  min-height: 350px;
  position: relative;
}

.split-map-section .mc-map-wrap {
  flex: 1;
  min-height: 350px;
}

/* Compact controls for split view */
.split-map-section .mc-map-layers { top: 6px; left: 6px; }
.split-map-section .mc-map-time { bottom: 6px; padding: 3px 6px; }

/* Chart + Grid Split View */
.chartgrid-view-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 400px;
  overflow: hidden;
  position: relative;
}

.chartgrid-chart-section {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  background: white;
  min-height: 120px;
  overflow: hidden;
}

.chartgrid-chart-section .comparison-chart {
  flex: 1;
  min-height: 100px;
}

.chartgrid-chart-section .comparison-chart svg {
  width: 100%;
  height: 100%;
}

.chart-legend--compact {
  padding: 4px 16px 6px;
  flex-shrink: 0;
}

.chart-legend--compact .legend-item {
  font-size: 11px;
  padding: 2px 6px;
}

.chartgrid-table-section {
  flex: 1;
  overflow: auto;
  background: white;
  min-height: 120px;
}

.chartgrid-table-section .table {
  font-size: 11px;
  margin-bottom: 0;
}

.chartgrid-table-section .sticky-col {
  position: sticky;
  left: 0;
  background: white;
  z-index: 1;
  font-size: 11px;
}

.chartgrid-table-section thead {
  position: sticky;
  top: 0;
  z-index: 2;
  background: white;
}

.chartgrid-table-section thead th {
  background: white;
  font-size: 11px;
  white-space: nowrap;
}

.chartgrid-table-section thead .sticky-col {
  z-index: 3;
}

/* Mode Toggle Bar */
.mode-toggle-bar {
  display: flex;
  align-items: center;
  padding: 6px 0;
  margin-bottom: 8px;
  gap: 2px;
}

.mode-btn {
  flex: 1;
  padding: 6px 10px;
  font-size: 11px;
  font-weight: 500;
  border: 1px solid #dee2e6;
  background: #fff;
  color: #495057;
  cursor: pointer;
  transition: all 0.15s ease;
}

.mode-btn:first-child {
  border-radius: 4px 0 0 4px;
}

.mode-btn:nth-child(2) {
  border-radius: 0 4px 4px 0;
  border-left: none;
}

.mode-btn:hover {
  background: #f8f9fa;
}

.mode-btn.active {
  background: #1976D2;
  border-color: #1976D2;
  color: white;
}

/* Models Header Bar */
.models-header-bar {
  display: flex;
  justify-content: flex-end;
  padding: 2px 0 4px 0;
}

.clear-link {
  font-size: 10px;
  color: #6c757d;
  text-decoration: none;
}

.clear-link:hover {
  color: #dc3545;
  text-decoration: underline;
}

/* Combinations Group Header */
.combinations-header {
  background: #f8f9fa;
}

/* Model Combinations items */
.combo-item {
  padding: 5px 4px !important;
  border-left: 3px solid transparent;
  border-radius: 3px;
  margin-bottom: 2px;
  transition: background 0.15s;
}

.combo-item:hover {
  background: #f0f4f8;
}

.combo-default-v1 {
  border-left-color: #000;
}

.combo-default-v2 {
  border-left-color: #2E7D32;
}

.combo-global {
  border-left-color: #1565C0;
}

.combo-conus {
  border-left-color: #E65100;
}

.combo-label {
  display: flex;
  flex-direction: column;
  line-height: 1.2;
}

.combo-desc {
  font-size: 9px;
  color: #888;
  margin-top: 1px;
}

.combo-badge {
  font-size: 9px;
  padding: 1px 5px;
  border-radius: 3px;
  font-weight: 600;
  margin-left: auto;
  flex-shrink: 0;
  line-height: 1.4;
}

.combo-badge-v1 {
  background: #e0e0e0;
  color: #555;
}

.combo-badge-v2 {
  background: #e8f5e9;
  color: #2E7D32;
}

.combo-badge-v2ext {
  background: #f3e5f5;
  color: #6A1B9A;
}

.combo-badge-global {
  background: #e3f2fd;
  color: #1565C0;
}

.combo-badge-conus {
  background: #fff3e0;
  color: #E65100;
}

/* Selected Runs List */
.selected-runs-list {
  margin-top: 8px;
  padding: 8px;
  background: #f5f5f5;
  border-radius: 4px;
  border: 1px solid #ddd;
}

.selected-runs-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 11px;
  font-weight: 600;
  color: #555;
  margin-bottom: 6px;
}

.selected-runs-items {
  max-height: 150px;
  overflow-y: auto;
}

.selected-run-item {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 6px;
  margin-bottom: 2px;
  background: white;
  border-radius: 3px;
  font-size: 11px;
  border-left: 3px solid #2196F3;
}

.selected-run-item .run-model-name {
  font-weight: 500;
  color: #333;
}

.selected-run-item .run-time {
  color: #666;
  font-family: monospace;
  font-size: 10px;
  flex: 1;
}

.selected-run-item button {
  padding: 0 4px;
  color: #999;
  background: none;
  border: none;
}

.selected-run-item button:hover {
  color: #dc3545;
}

/* Model Runs Container */
.model-runs-container {
  padding: 8px 0;
}

.runs-loading,
.no-runs-msg {
  padding: 12px;
  text-align: center;
  color: #6c757d;
  font-size: 11px;
}

.model-runs-group {
  margin-bottom: 12px;
}

.model-runs-title {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  color: #333;
  margin-bottom: 6px;
  padding-bottom: 4px;
  border-bottom: 1px solid #eee;
}

.model-color-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
}

.runs-by-date-container {
  max-height: 110px;
  overflow-y: auto;
  padding-right: 4px;
}

.runs-date-group {
  display: flex;
  align-items: flex-start;
  margin-bottom: 6px;
  gap: 8px;
}

.runs-date-label {
  font-size: 10px;
  color: #666;
  white-space: nowrap;
  padding-top: 4px;
  min-width: 70px;
  font-weight: 500;
}

.runs-time-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 3px;
  flex: 1;
}

.run-time-btn {
  padding: 3px 6px;
  font-size: 10px;
  font-family: monospace;
  border: 1px solid #dee2e6;
  border-radius: 3px;
  background: #fff;
  color: #495057;
  cursor: pointer;
  transition: all 0.15s ease;
}

.run-time-btn:hover {
  background: #e9ecef;
  border-color: #adb5bd;
}

.run-time-btn.selected {
  background: #1976D2;
  border-color: #1976D2;
  color: white;
}

/* Scrollbar styling for runs */
.runs-by-date-container::-webkit-scrollbar {
  width: 4px;
}

.runs-by-date-container::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 2px;
}

.runs-by-date-container::-webkit-scrollbar-thumb {
  background: #ccc;
  border-radius: 2px;
}

.runs-by-date-container::-webkit-scrollbar-thumb:hover {
  background: #aaa;
}

/* ===== Quick Compare Modal ===== */
.qc-modal-header {
  background: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
  padding: 12px 16px;
}

.qc-modal-header .modal-title {
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

.qc-modal-body {
  padding: 0;
  height: 75vh;
  overflow-y: auto;
}

/* Summary bar */
.qc-summary-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 16px;
  background: #f8f9fa;
  border-bottom: 1px solid #eee;
  font-size: 12px;
  color: #555;
  flex-wrap: wrap;
  gap: 8px;
}

.qc-summary-left {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
}

.qc-summary-label {
  color: #888;
}

.qc-summary-sep {
  color: #ccc;
  margin: 0 2px;
}

.qc-summary-counts {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.qc-count {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
}

.qc-count-missing { background: #ffcdd2; color: #b71c1c; }
.qc-count-partial { background: #fff3e0; color: #e65100; }
.qc-count-nointerp { background: #e3f2fd; color: #1565c0; }
.qc-count-ok { background: #c8e6c9; color: #1b5e20; }

/* Filter bar */
.qc-filter-bar {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border-bottom: 1px solid #eee;
  flex-wrap: wrap;
}

.qc-filter-label {
  font-size: 11px;
  color: #888;
  font-weight: 500;
  margin-right: 2px;
}

.qc-filter-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 10px;
  border: 1px solid #ccc;
  border-radius: 14px;
  background: #fff;
  font-size: 11px;
  font-weight: 500;
  color: #666;
  cursor: pointer;
  transition: all 0.15s;
}

.qc-filter-btn:hover {
  border-color: #999;
  background: #f5f5f5;
}

.qc-filter-btn.active {
  background: #e8eaf6;
  border-color: #5c6bc0;
  color: #283593;
}

.qc-filter-btn i {
  font-size: 11px;
}

.qc-filter-count {
  font-size: 11px;
  color: #999;
  margin-left: auto;
}

.qc-empty-filter {
  text-align: center;
  padding: 32px 16px;
  color: #999;
  font-size: 13px;
}

/* Table wrapper */
.qc-table-wrap {
  padding: 0 16px 8px;
}

/* Quick Compare Table */
.qc-table {
  font-size: 12px;
  margin-bottom: 0;
}

.qc-table thead th {
  position: sticky;
  top: 0;
  background: #fff;
  z-index: 2;
  white-space: nowrap;
  border-bottom: 2px solid #dee2e6;
}

.qc-th-element {
  min-width: 140px;
  font-weight: 600;
}

.qc-th-model {
  text-align: center;
  font-size: 11px;
  max-width: 140px;
  overflow: hidden;
  text-overflow: ellipsis;
}

.qc-row {
  border-left: 3px solid transparent;
}

.qc-row:hover {
  background: #f8f9fa;
}

.qc-row-issue {
  border-left-color: #ef6c00;
}

.qc-cell-element {
  font-weight: 500;
  white-space: nowrap;
}

.qc-element-link {
  color: #333;
  text-decoration: none;
  border-bottom: 1px dashed #bbb;
  cursor: pointer;
}

.qc-element-link:hover {
  color: #1565c0;
  border-bottom-color: #1565c0;
}

.qc-cell {
  text-align: center;
  vertical-align: middle;
  padding: 6px 8px !important;
  min-width: 90px;
}

.qc-cell-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
}

.qc-cell-icons {
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: 11px;
}

.qc-icon-label {
  display: flex;
  align-items: center;
  gap: 3px;
  font-weight: 500;
  white-space: nowrap;
}

.qc-icon-label i {
  font-size: 12px;
}

.qc-cell-stats {
  display: flex;
  gap: 6px;
  align-items: center;
  font-size: 11px;
}

.qc-cell-missing-label {
  font-size: 10px;
  color: #999;
  font-style: italic;
}

.qc-cell-range {
  font-size: 10px;
  color: #78909c;
  white-space: nowrap;
}

.qc-cell-range i {
  font-size: 9px;
  margin-right: 1px;
}

.qc-icon-range {
  color: #78909c;
}

/* Cell background colors */
.qc-cell-missing {
  background: #ffebee !important;
}

.qc-cell-partial {
  background: #fff8e1 !important;
}

.qc-cell-nointerp {
  background: #e3f2fd !important;
}

.qc-cell-partial-nointerp {
  background: linear-gradient(135deg, #fff8e1 50%, #e3f2fd 50%) !important;
}

.qc-cell-ok {
  background: transparent;
}

/* Icons */
.qc-icon-missing {
  color: #d32f2f;
}

.qc-icon-partial {
  color: #ef6c00;
}

.qc-icon-nointerp {
  color: #1976d2;
}

/* Coverage */
.qc-coverage {
  font-weight: 600;
}

.qc-coverage-good {
  color: #2e7d32;
}

.qc-coverage-warn {
  color: #ef6c00;
}

.qc-coverage-bad {
  color: #d32f2f;
}

/* Spread */
.qc-spread {
  font-family: monospace;
  font-size: 11px;
  padding: 1px 5px;
  border-radius: 3px;
}

.qc-spread-low {
  background: #e8f5e9;
  color: #2e7d32;
}

.qc-spread-med {
  background: #fff3e0;
  color: #e65100;
}

.qc-spread-high {
  background: #ffebee;
  color: #c62828;
  font-weight: 700;
}

.qc-spread-sample {
  font-family: monospace;
  font-size: 10px;
  padding: 1px 4px;
  border-radius: 3px;
}

/* Legend */
.qc-legend {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  padding: 10px 0 4px;
  border-top: 1px solid #eee;
  font-size: 11px;
  color: #666;
}

.qc-legend-item {
  display: flex;
  align-items: center;
  gap: 4px;
}
</style>
